import React, { useEffect, useState, useMemo } from 'react';
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Layout from '../../Component/Layout';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useFieldPermissions } from "../../utils/useFieldPermissions";
import { authHeaders } from "../../utils/authHeaders";
import MaskedValue from "../../Component/MaskedValue";
import { FileText } from 'lucide-react';

// ── Row tint by posting state (same light-tint style as the expired rows) ──
// green = fully posted, yellow = partly posted, red = not posted
const POSTING_ROW_BG = {
  POSTED:     "#dcfce7",
  PARTIAL:    "#fef9c3",
  NOT_POSTED: "#fde8e8",
};


// Reads module-level permissions (can_view / can_create / can_edit /
// can_approve / can_convert) for the given module_id from the logged-in
// user's stored permissions, the same array authController.js returns at
// login. Covers all 5 role types: CREATE (can_create), DISPLAY (can_view),
// CHANGE (can_view + can_edit), REVIEW (can_view + can_approve), and
// CONVERT (can_view + can_convert). Admins get an empty `permissions`
// array from the backend by design, so they're granted full access here
// rather than being locked out by an "empty permissions" default.
// (Mirrors the same helper in enquiry.js — kept local per-file rather than
// shared, matching the existing codebase pattern.)
const getModulePermissions = (moduleId) => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return { canView: false, canCreate: false, canEdit: false, canApprove: false, canConvert: false };
    if (user.role === "admin") return { canView: true, canCreate: true, canEdit: true, canApprove: true, canConvert: true };

    const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
    return {
      canView: perm?.can_view === 1,
      canCreate: perm?.can_create === 1,
      canEdit: perm?.can_edit === 1,
      canApprove: perm?.can_approve === 1,
      canConvert: perm?.can_convert === 1
    };
  } catch (e) {
    console.error("Error reading user permissions:", e);
    return { canView: false, canCreate: false, canEdit: false, canApprove: false, canConvert: false };
  }
};

// Compact react-select style matching PO modal
const rsCompact = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "13px",
    borderColor: "#ced4da",
    boxShadow: "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "13px", position: "absolute" }),
};

const USER_MANUAL_SCOPE = {
    moduleName: "Procurement",
    process: "Purchase",
    subProcess: "Purchase Request"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

function PurchaseRequisition() {
  const location = useLocation();
  const navigate = useNavigate();

  const [productPlanningData, setProductPlanningData] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [createActiveTab, setCreateActiveTab] = useState("product");
  const [modalData, setModalData] = useState(null);
  const [productCodes, setProductCodes] = useState([]);
  // Asset Master records, same shape as productCodes — used when Product
  // Type = Asset in the Create/Edit PR modal (see modalTypeProductCodes).
  const [assetCodes, setAssetCodes] = useState([]);
  const [purchaseRequestOptions, setPurchaseRequestOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionResult, setConversionResult] = useState(null); // { message, data: [{prNo, poId}] }
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [isCheckingSuppliers, setIsCheckingSuppliers] = useState(false);
  const [showSupplierDialog, setShowSupplierDialog] = useState(false);
  const [supplierDialogItems, setSupplierDialogItems] = useState([]); // [{purchase_request_no, product_code, vendorCode, vendorName}]
  const [supplierDialogOptions, setSupplierDialogOptions] = useState([]); // distinct suppliers
  const [selectedForcedSupplier, setSelectedForcedSupplier] = useState(null);
  const [pendingConvertItems, setPendingConvertItems] = useState([]);
  // Row keys currently ticked in the Review tab. Row 0 (the product the modal
  // was opened for) is keyed "row0"; sibling product lines use their own
  // _rowId. Kept as a Set so each line item can be approved/rejected on its
  // own instead of the whole PR moving together.
  const [reviewSelectedRows, setReviewSelectedRows] = useState(new Set());
  const [changeLogs, setChangeLogs] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [uploadedDocs, setUploadedDocs] = useState([]);
  // PR document upload size limit — configurable via Configuration →
  // File Size Config (Module = "Purchase Requisition"). Falls back to
  // 50 KB (the old hardcoded value) if it can't be fetched.
  const DEFAULT_MAX_PR_DOC_SIZE_KB = 50;
  const [maxPrDocSizeKb, setMaxPrDocSizeKb] = useState(DEFAULT_MAX_PR_DOC_SIZE_KB);
  const [showPRDocInModal, setShowPRDocInModal] = useState(false);
  const [commentModal, setCommentModal] = useState(false);
  const [comment, setComment] = useState("");
  const [actionType, setActionType] = useState("");
  // Whether Purchase Organization/Planner/Location were already persisted on
  // the backend at the moment the PR was opened — captured BEFORE the
  // single-option auto-fill runs, so auto-filling the form locally doesn't
  // by itself unlock the Docs tab. Only an actual Save (which persists
  // these fields) should do that; see isUnsavedPlanningDraft below.
  const [coreFieldsSavedAtOpen, setCoreFieldsSavedAtOpen] = useState(false);

  // Active / Posted toggle
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem("pr_active_tab") || "active");

  // Driven by the Navbar alert bell: clicking "Purchase Requisition Validity
  // Expired" navigates here with { showExpiredOnly: true }. expiredKeys
  // holds the purchase_request_no of every PR /purchase-request/validity/expired
  // currently flags — used both to filter the list down to just those rows
  // and to highlight them below. Same pattern as SalesOrder.js / Home.js.
  const [showExpiredOnly, setShowExpiredOnly] = useState(!!location.state?.showExpiredOnly);
  const [expiredKeys, setExpiredKeys] = useState(new Set());

  // The line above only fires ONCE, on mount. If you're already sitting on
  // this page when the Navbar bell is clicked, react-router navigates to the
  // SAME route — this component never remounts, so that useState initializer
  // never re-runs and showExpiredOnly stays stuck at whatever it was on first
  // load. This effect re-checks location.state on every navigation (including
  // same-route ones) so clicking the bell while already here still works.
  // Keyed off location.key (unique per navigation) rather than location.state
  // itself — same reasoning as Home.js's equivalent effect (a repeat bell
  // click always passes the same literal `true`, so keying off the value
  // would only fire the first time).
  useEffect(() => {
    if (location.state?.showExpiredOnly) {
      setShowExpiredOnly(true);
      // Consume the navigation flag so it doesn't live on in browser
      // history — otherwise a plain refresh replays this same state and
      // re-applies the expired-only filter forever, with no way to land
      // back on a normal, unfiltered page load. Same fix as Home.js.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Backs the Navbar alert bell (and the "Purchase Requisition Validity
  // Expired" highlight/filter below) — GET /purchase-request/validity/expired,
  // keyed by purchase_request_no. Fetched once on mount, same as Sales Order.
  useEffect(() => {
    const fetchExpiredValidity = async () => {
      try {
        const res = await axios.get(`${PURCHASE_API}/purchase-request/validity/expired`, { headers: authHeaders() });
        const rows = res.data || [];
        setExpiredKeys(new Set(rows.map(r => r.purchase_request_no)));
      } catch (err) {
        console.error("Failed to load expired validity list:", err);
      }
    };
    fetchExpiredValidity();
  }, []);

  // Filter states
  //eslint-disable-next-line
  const [selectedCategory, setSelectedCategory] = useState(null);
  //eslint-disable-next-line
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedProductCode, setSelectedProductCode] = useState(null);
  const [selectedPurchaseRequestNo, setSelectedPurchaseRequestNo] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  //eslint-disable-next-line
  const [selectedPurchaseOrganization, setSelectedPurchaseOrganization] = useState(null);

  // Cable attribute filters (mirrors Stock Report / BatchList: Construction,
  // Code, Cable Type, Category, Cores, Color, Cross Section, Std. Ref, Voltage)
  const [selectedConstruction, setSelectedConstruction] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedCableType, setSelectedCableType] = useState(null);
  const [selectedCableCategory, setSelectedCableCategory] = useState(null);
  const [selectedCores, setSelectedCores] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedCrossSection, setSelectedCrossSection] = useState(null);
  const [selectedStdRef, setSelectedStdRef] = useState(null);
  const [selectedVoltage, setSelectedVoltage] = useState(null);


  // Dropdown options
  const [purchaseOrgOptions, setPurchaseOrgOptions] = useState([]);
  const [purchasePlannerOptions, setPurchasePlannerOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  //eslint-disable-next-line
  const [categoryOptions, setCategoryOptions] = useState([]);
  //eslint-disable-next-line
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  // Static filter options for the list page Status filter — mirrors the
  // raw approval_status values written by the backend. Filtering runs at
  // the line-item level (see filteredData below), same as Product Code,
  // so the derived "Partially Approved/Rejected" labels (which only exist
  // once lines are grouped under a PR — see uniqueStatuses logic further
  // down) aren't offered here since no single line ever carries them.
  const statusFilterOptions = [
    { value: "CREATED", label: "Created" },
    { value: "PENDING FOR APPROVAL", label: "Pending For Approval" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
  ];

  const [currentUser, setCurrentUser] = useState("");
  const [page, setPage] = useState(1);
  const [isEditMode, setIsEditMode] = useState(false);

  // Role-based field visibility/edit for this module. isFieldVisible masks a
  // value in view mode (DISPLAY-type roles); isFieldEditable locks a field
  // from being changed in edit mode (CHANGE-type roles with that field
  // unchecked in Field Access). Same hook enquiry.js uses.
  const { isFieldVisible, isFieldEditable } = useFieldPermissions("PURCHASE_REQ");

  // Module-level access for this page. CREATE-type roles are add-only
  // (canView false, canCreate true) — they should see the Create button and
  // form but not the existing-requisitions list. DISPLAY-type roles are the
  // opposite (canView true, canCreate false) — they can browse but the
  // Create button should be hidden.
  const { canView, canCreate, canEdit, canApprove, canConvert } = useMemo(() => getModulePermissions("PURCHASE_REQ"), []);

  // A field is locked (read-only) while editing an existing PR if the
  // logged-in user's role has that field unchecked in Field Access. Does
  // NOT apply to brand-new PRs (isEditMode is true there too, but a
  // create-only user should be able to fill every field on a fresh form —
  // field-level restriction only matters when changing something that
  // already exists). We approximate "existing record" as modalData having
  // a saved purchase_request_no / pl_number already.
  const isExistingRecord = !!(modalData?.purchase_request_no || modalData?.pl_number);
  const isFieldLocked = (fieldId) => isEditMode && isExistingRecord && !isFieldEditable(fieldId);
  //eslint-disable-next-line
  const [pirChars, setPirChars] = useState([]);        // [{characteristic, value}]
  //eslint-disable-next-line
  const [pirCharsPirId, setPirCharsPirId] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);

  // ── Product tab filters inside Create/Edit PR modal ──
  const [modalFilterCategory, setModalFilterCategory] = useState(null);
  const [modalFilterSubCategory, setModalFilterSubCategory] = useState(null);

  // ── Additional product line items when creating a NEW PR (row 0 lives in modalData) ──
  // Each row now also carries its OWN purchase-info-record data so
  // Characteristics are looked up per product, not shared from row 0.
  const [extraProducts, setExtraProducts] = useState([]); // [{ _rowId, product_code, productDescription, uom, hsnCode, requirement_qty, requirement_date, productType, productCategory, subCategory, product_class, pirNumber, pirChars }]

  const getCurrentUser = () => {
    const userObj = localStorage.getItem("user");
    if (userObj) {
      try {
        const parsedUser = JSON.parse(userObj);
        // Prefer the full name (first + last) so Created By / Approver show a
        // real name; fall back to the combined name, then user id, then email
        // for accounts with no name saved yet.
        const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
        if (fullName) return fullName;
        if (parsedUser.name && parsedUser.name.trim()) return parsedUser.name.trim();
        if (parsedUser.userId) return parsedUser.userId;
        if (parsedUser.email) return parsedUser.email;
      } catch (e) { /* ignore */ }
    }
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
    const email = localStorage.getItem("email");
    if (email && email !== "undefined" && email !== "null") return email;
    return localStorage.getItem("userName") || "User";
  };

  const fetchLogs = async (prNo) => {
    if (!prNo) { setChangeLogs([]); return; }
    try {
      const res = await axios.get(`${PURCHASE_API}/purchase-request/logs/${encodeURIComponent(prNo)}`, { headers: authHeaders() });
      if (res.data.success) setChangeLogs(res.data.logs || []);
    } catch (err) {
      console.error("Error fetching PR logs:", err);
    }
  };

  useEffect(() => {
    fetch(`${MASTER_API}/configuration/file-size-limit/${encodeURIComponent("Purchase Requisition")}`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        if (data && data.maxSizeKb) setMaxPrDocSizeKb(data.maxSizeKb);
      })
      .catch(() => { /* keep default limit if config fetch fails */ });
  }, []);

// File picker for the Docs tab — uploads immediately to Google Drive via
// purchase-request/upload-docs/:prNo (same pattern as Sales Order's Docs
// tab). Requires the PR to already have a purchase_request_no / pl_number;
// the upload button is only shown once that exists (see Docs tab render).
const [docsUploading, setDocsUploading] = useState(false);

const fetchUploadedDocs = (prNo) => {
  if (!prNo) { setUploadedDocs([]); return; }
  axios.get(`${PURCHASE_API}/purchase-request/list-docs/${encodeURIComponent(prNo)}`, { headers: authHeaders() })
    .then(res => setUploadedDocs(res.data?.files || []))
    .catch(() => setUploadedDocs([]));
};

const handleDocUpload = async (e) => {
  const rawFiles = Array.from(e.target.files);
  e.target.value = ""; // allow re-selecting the same file name later

  const prNo = modalData?.purchase_request_no || modalData?.pl_number || "";
  if (!prNo) {
    toast.error("Save the PR before attaching documents.");
    return;
  }

  const maxBytes = maxPrDocSizeKb * 1024;
  const okFiles = [];
  const rejected = [];
  rawFiles.forEach((f) => {
    if (f.size > maxBytes) rejected.push(f.name);
    else okFiles.push(f);
  });

  if (rejected.length) {
    toast.error(
      `${rejected.length} file(s) exceed the ${maxPrDocSizeKb} KB limit and were skipped: ${rejected.join(", ")}`
    );
  }

  if (!okFiles.length) return;

  const formData = new FormData();
  okFiles.forEach((f) => formData.append("docs", f));

  setDocsUploading(true);
  try {
    await axios.post(
      `${PURCHASE_API}/purchase-request/upload-docs/${encodeURIComponent(prNo)}`,
      formData,
      { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
    );
    fetchUploadedDocs(prNo);
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to upload document(s)");
  } finally {
    setDocsUploading(false);
  }
};

const handleDocDownload = async (doc) => {
  const prNo = modalData?.purchase_request_no || modalData?.pl_number || "";
  if (!prNo || !doc.filename) return;
  try {
    const res = await fetch(
      `${PURCHASE_API}/purchase-request/download-doc/${encodeURIComponent(prNo)}/${encodeURIComponent(doc.filename)}`,
      { headers: authHeaders() }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.originalname || doc.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err.message || "Failed to download file");
  }
};

  const fetchSummary = () => {
    axios.get(`${PURCHASE_API}/purchase-request/summary`, { headers: authHeaders() })
      .then(res => {
        if (res.data.success) {
          const data = res.data.data;
          setProductPlanningData(data);
          const prSet = new Map();
          data.forEach(item => {
            if (item.purchase_request_no && !prSet.has(item.purchase_request_no))
              prSet.set(item.purchase_request_no, item.purchase_request_no);
          });
          setPurchaseRequestOptions(Array.from(prSet.keys()).map(pr => ({ label: pr, value: pr })));
        }
      })
      .catch(err => console.error("Summary Fetch Error:", err));
  };

  useEffect(() => {
    fetchSummary();

    axios.get(`${PURCHASE_API}/purchase-request/product-codes`, { headers: authHeaders() })
      .then(res => {
        if (res.data.success) {
          setProductCodes(res.data.data.map(i => ({
            label: i.productDescription ? `${i.productCode} - ${i.productDescription}` : i.productCode,
            value: i.productCode,
            product: i
          })));
        }
      })
      .catch(err => console.error("Product Codes Error:", err));

    axios.get(`${PURCHASE_API}/purchase-request/asset-codes`, { headers: authHeaders() })
      .then(res => {
        if (res.data.success) {
          setAssetCodes(res.data.data.map(i => ({
            label: i.productDescription ? `${i.productCode} - ${i.productDescription}` : i.productCode,
            value: i.productCode,
            product: i
          })));
        }
      })
      .catch(err => console.error("Asset Codes Error:", err));

    axios.get(`${PURCHASE_API}/purchase-request/purchase-orgs`, { headers: authHeaders() })
      .then(res => { if (res.data.success) setPurchaseOrgOptions(res.data.data.map(i => ({ label: i.name, value: i.name }))); })
      .catch(err => console.error("Purchase Org Error:", err));

    axios.get(`${PURCHASE_API}/purchase-request/purchase-planners`, { headers: authHeaders() })
      .then(res => { if (res.data.success) setPurchasePlannerOptions(res.data.data.map(i => ({ label: i.name, value: i.name }))); })
      .catch(err => console.error("Purchase Planner Error:", err));

    axios.get(`${MASTER_API}/configuration/location`, { headers: authHeaders() })
      .then(res => setLocationOptions((res.data || []).map(i => ({ label: i.name, value: i.name }))))
      .catch(err => console.error("Location Error:", err));

  }, []);

  // When a PR opened from Planning has only ONE option available for
  // Purchase Organization / Purchase Planner / Location, auto-select it
  // instead of leaving the field blank for the user to pick from a
  // single-item dropdown. Only applies to planning-origin PRs (pl_number
  // set) and never overwrites a value that's already there.
  useEffect(() => {
    if (!showModal || !modalData?.pl_number) return;
    setModalData(prev => {
      if (!prev) return prev;
      let changed = false;
      const next = { ...prev };
      if (!next.purchase_organization && purchaseOrgOptions.length === 1) {
        next.purchase_organization = purchaseOrgOptions[0].value;
        changed = true;
      }
      if (!next.purchase_planner && purchasePlannerOptions.length === 1) {
        next.purchase_planner = purchasePlannerOptions[0].value;
        changed = true;
      }
      if (!next.location && locationOptions.length === 1) {
        next.location = locationOptions[0].value;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [showModal, modalData?.pl_number, purchaseOrgOptions, purchasePlannerOptions, locationOptions]);

  useEffect(() => {
    const openPrNo = location.state?.openPrNo;
    if (openPrNo && productPlanningData.length > 0) {
      const matchedPR = productPlanningData.find(
        (pr) => pr.purchase_request_no === openPrNo || pr.pl_number === openPrNo
      );
      if (matchedPR) {
        handleViewClick(matchedPR.product_code, matchedPR.purchase_request_no, matchedPR.approval_status);
      }
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, productPlanningData]);

  useEffect(() => { setCurrentUser(getCurrentUser()); }, []);

  useEffect(() => {
    // Purchase Requisition is the "Internal" side (mirrors Sales Order /
    // Quotation / Enquiry on the "External" side) — only Product Types
    // tagged Internal in Product Master should appear here. Previously this
    // called /configuration/product-types with no usage filter at all, so
    // Semi Finished / Finished Goods (tagged External) showed up here too.
    axios.get(`${MASTER_API}/configuration/product-types?usage=Internal`, { headers: authHeaders() })
      .then(res => setProductTypeOptions((res.data || []).map(item => ({ value: item.name, label: item.name }))));
    axios.get(`${MASTER_API}/configuration/category`, { headers: authHeaders() })
      .then(res => setCategoryOptions((res.data || []).map(item => ({ value: item.name, label: item.name }))));
    axios.get(`${MASTER_API}/configuration/sub-category`, { headers: authHeaders() })
      .then(res => setSubCategoryOptions((res.data || []).map(item => ({ value: item.name, label: item.name }))));
  }, []);

  // A PR line belongs in Posted only once it's actually been converted to a
  // PO. A rejected line has nothing further to do, but it still needs to be
  // visible (and stay put) in the Active tab, not jump to Posted.
  const isFinalPR = (item) => item.pr_status === 'CONVERTED';
  const activeData = productPlanningData.filter(item => !isFinalPR(item));
  const postedData = productPlanningData.filter(item => isFinalPR(item));
  const activeCount = activeData.length;
  const postedCount = postedData.length;
  const tabData = activeTab === "active" ? activeData : postedData;

  // Case/whitespace-insensitive compare for Product Type strings. The
  // Product Type dropdown options come from /configuration/product-types,
  // while each product record's own productType field comes from a
  // separate master (/purchase-request/product-codes). If the two masters
  // don't spell a type identically (casing, trailing spaces, "Packing" vs
  // "Packing "), a strict === match silently returns zero products.
  const normalizeType = (s) => (s || '').toString().trim().toLowerCase();

  // Category / Sub Category / Product Code options for the PR modal's Product
  // section — derived directly from productCodes/assetCodes (scoped to the
  // Product Type selected in the modal) so they always match what's actually
  // on the product/asset records, instead of the separate master
  // /configuration/category & /configuration/sub-category lists which can
  // contain unrelated values (finished goods, etc.) or mismatched spelling/casing.
  // Strictly scoped to whichever Product Type is selected — Asset -> asset
  // codes, any other type -> only that type's product codes, nothing
  // selected yet -> defaults to Raw Material.
  const modalTypeProductCodes = React.useMemo(() => {
    const type = modalData?.productType;
    if (type === 'Asset') return assetCodes;
    const targetType = type || 'Raw Material';
    return productCodes.filter(p => normalizeType(p.product?.productType) === normalizeType(targetType));
  }, [productCodes, assetCodes, modalData?.productType]);

  // Combined list (both product & asset codes) — used only to resolve a
  // display value for an already-selected code, regardless of which list
  // it currently lives in.
  const allCodeOptions = React.useMemo(
    () => [...productCodes, ...assetCodes],
    [productCodes, assetCodes]
  );

  // Product Code filter options for the LIST PAGE filter bar — scoped to
  // whichever Product Type is selected there (mirrors modalTypeProductCodes,
  // but driven by selectedProductType instead of modalData.productType).
  // Asset -> asset codes only, another type -> only that type's product
  // codes, nothing selected -> everything (previous behavior).
  const listFilterCodeOptions = React.useMemo(() => {
    const type = selectedProductType?.value;
    if (type === 'Asset') return assetCodes;
    if (type) return productCodes.filter(p => normalizeType(p.product?.productType) === normalizeType(type));
    return allCodeOptions;
  }, [selectedProductType, productCodes, assetCodes, allCodeOptions]);

  const modalCategoryOptions = React.useMemo(() => {
    const names = [...new Set(modalTypeProductCodes.map(p => p.product?.productCategory).filter(Boolean))];
    return names.sort().map(name => ({ value: name, label: name }));
  }, [modalTypeProductCodes]);

  const modalSubCategoryOptions = React.useMemo(() => {
    if (!modalFilterCategory) return [];
    const names = [...new Set(
      modalTypeProductCodes
        .filter(p => p.product?.productCategory === modalFilterCategory.value)
        .map(p => p.product?.subCategory)
        .filter(Boolean)
    )];
    return names.sort().map(name => ({ value: name, label: name }));
  }, [modalTypeProductCodes, modalFilterCategory]);

  const modalFilteredProductCount = React.useMemo(() => {
    return modalTypeProductCodes.filter(p =>
      (!modalFilterCategory || p.product?.productCategory === modalFilterCategory.value) &&
      (!modalFilterSubCategory || p.product?.subCategory === modalFilterSubCategory.value)
    ).length;
  }, [modalTypeProductCodes, modalFilterCategory, modalFilterSubCategory]);

  // ── Cable attribute filter options (Construction..Voltage) ──────────────────
  // Built ONLY from the PR rows of the selected Product Type (all rows when
  // none is selected), so no other-type values appear. Options also cascade: each dropdown only lists values
  // that still exist after applying every OTHER attribute filter.
  const cableScopeType = selectedProductType?.value || "";
  const cableScopedRows = React.useMemo(
    () => cableScopeType
      ? productPlanningData.filter(r => normalizeType(r.productType) === normalizeType(cableScopeType))
      : productPlanningData,
    [productPlanningData, cableScopeType]
  );

  // Cable attributes (Construction..Voltage) only exist on cable products.
  // Raw Material / other types have none, so for them show Category /
  // Sub Category instead of a row of empty "No options" dropdowns.
  const hasCableAttrs = React.useMemo(
    () => cableScopedRows.some(r =>
      ["construction", "code", "cableType", "noOfCores", "color", "nominalCrossSection", "referenceOfStandard", "voltage"]
        .some(k => r[k] !== undefined && r[k] !== null && String(r[k]).trim() !== "")
    ),
    [cableScopedRows]
  );

  const rmCategoryOptions = React.useMemo(() => {
    const names = [...new Set(cableScopedRows.map(r => r.productCategory).filter(Boolean).map(v => String(v).trim()))];
    return names.sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [cableScopedRows]);

  const rmSubCategoryOptions = React.useMemo(() => {
    const names = [...new Set(
      cableScopedRows
        .filter(r => !selectedCategory || String(r.productCategory || "").trim() === selectedCategory.value)
        .map(r => r.subCategory).filter(Boolean).map(v => String(v).trim())
    )];
    return names.sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [cableScopedRows, selectedCategory]);

  const cableAttrSelections = React.useMemo(() => ({
    construction: selectedConstruction?.value,
    code: selectedCode?.value,
    cableType: selectedCableType?.value,
    category: selectedCableCategory?.value,
    noOfCores: selectedCores?.value,
    color: selectedColor?.value,
    nominalCrossSection: selectedCrossSection?.value,
    referenceOfStandard: selectedStdRef?.value,
    voltage: selectedVoltage?.value
  }), [
    selectedConstruction, selectedCode, selectedCableType, selectedCableCategory,
    selectedCores, selectedColor, selectedCrossSection, selectedStdRef, selectedVoltage
  ]);

  // Row field for each filter (Category filter is the product's cable category)
  const cableRowField = (key) => (key === "category" ? "productCategory" : key);

  const getCableAttrOptions = React.useCallback((key) => {
    const rows = cableScopedRows.filter(r =>
      Object.entries(cableAttrSelections).every(([k, v]) =>
        k === key || !v || String(r[cableRowField(k)] ?? "").trim() === v
      )
    );
    const uniqueVals = Array.from(
      new Set(
        rows
          .map(r => r[cableRowField(key)])
          .filter(v => v !== undefined && v !== null && v !== "")
          .map(v => String(v).trim())
          .filter(v => v !== "")
      )
    );
    return uniqueVals
      .map(v => ({ value: v, label: v }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [cableScopedRows, cableAttrSelections]);

  const constructionOptions = React.useMemo(() => getCableAttrOptions("construction"), [getCableAttrOptions]);
  const codeOptions = React.useMemo(() => getCableAttrOptions("code"), [getCableAttrOptions]);
  const cableTypeOptions = React.useMemo(() => getCableAttrOptions("cableType"), [getCableAttrOptions]);
  const cableCategoryOptions = React.useMemo(() => getCableAttrOptions("category"), [getCableAttrOptions]);
  const coresOptions = React.useMemo(() => getCableAttrOptions("noOfCores"), [getCableAttrOptions]);
  const colorOptions = React.useMemo(() => getCableAttrOptions("color"), [getCableAttrOptions]);
  const crossSectionOptions = React.useMemo(() => getCableAttrOptions("nominalCrossSection"), [getCableAttrOptions]);
  const stdRefOptions = React.useMemo(() => getCableAttrOptions("referenceOfStandard"), [getCableAttrOptions]);
  const voltageOptions = React.useMemo(() => getCableAttrOptions("voltage"), [getCableAttrOptions]);

  const filteredData = React.useMemo(() => {
    return tabData.filter(item => (
      (!showExpiredOnly || expiredKeys.has(item.purchase_request_no)) &&
      (!selectedCategory || item.productCategory === selectedCategory.value) &&
      (!selectedSubCategory || item.subCategory === selectedSubCategory.value) &&
      (!selectedProductCode || item.product_code === selectedProductCode.value) &&
      (!selectedPurchaseRequestNo || item.purchase_request_no === selectedPurchaseRequestNo.value) &&
      (!selectedPurchaseOrganization || item.purchaseOrganization === selectedPurchaseOrganization.value) &&
      (!selectedProductType || item.productType === selectedProductType.value) &&
      (!selectedStatus || String(item.approval_status).toUpperCase() === selectedStatus.value) &&
      (!selectedConstruction || item.construction === selectedConstruction.value) &&
      (!selectedCode || item.code === selectedCode.value) &&
      (!selectedCableType || item.cableType === selectedCableType.value) &&
      (!selectedCableCategory || item.productCategory === selectedCableCategory.value) &&
      (!selectedCores || item.noOfCores === selectedCores.value) &&
      (!selectedColor || item.color === selectedColor.value) &&
      (!selectedCrossSection || item.nominalCrossSection === selectedCrossSection.value) &&
      (!selectedStdRef || item.referenceOfStandard === selectedStdRef.value) &&
      (!selectedVoltage || item.voltage === selectedVoltage.value)
    ));
  }, [
    tabData, showExpiredOnly, expiredKeys, selectedCategory, selectedSubCategory, selectedProductCode,
    selectedPurchaseRequestNo, selectedPurchaseOrganization, selectedProductType, selectedStatus,
    selectedConstruction, selectedCode, selectedCableType, selectedCableCategory,
    selectedCores, selectedColor, selectedCrossSection, selectedStdRef, selectedVoltage
  ]);

  // ── Group line items under one row per Purchase Request ──────────────────────
  // Every product line created together shares the same purchase_request_no
  // (see createPR on the backend). The list should show one row per PR, not
  // one row per product line, so group here before paginating.
  const groupedFilteredData = React.useMemo(() => {
    const map = new Map();
    filteredData.forEach(item => {
      const key = item.purchase_request_no || `__no_pr_${item.id}`;
      if (!map.has(key)) map.set(key, { purchase_request_no: item.purchase_request_no, items: [] });
      map.get(key).items.push(item);
    });
    const groups = Array.from(map.values());

    if (activeTab === "posted") {
      // Posted tab: show the most recently converted/rejected PRs first, using
      // the actual action timestamp (action_date, set the moment someone
      // approves/rejects, or created_at for converted rows) instead of the
      // creation id — an old PR rejected just now should still surface at the
      // top, even though its id was assigned long ago.
      const touchedAt = (item) => {
        const raw = item.action_date || item.created_at;
        const t = raw ? new Date(raw).getTime() : NaN;
        return Number.isNaN(t) ? (Number(item.id) || 0) : t;
      };
      groups.sort((a, b) => {
        const maxA = Math.max(...a.items.map(touchedAt));
        const maxB = Math.max(...b.items.map(touchedAt));
        return maxB - maxA;
      });
    } else {
      // Active tab: newest CREATED PR first. Several PRs can share the same
      // requirement_date, so fall back to created_at (and then id) as the
      // tie-breaker instead of leaving same-date rows in whatever order the
      // backend UNION happened to return them.
      const createdAt = (item) => {
        const raw = item.created_at;
        const t = raw ? new Date(raw).getTime() : NaN;
        return Number.isNaN(t) ? (Number(item.id) || 0) : t;
      };
      groups.sort((a, b) => {
        const maxA = Math.max(...a.items.map(createdAt));
        const maxB = Math.max(...b.items.map(createdAt));
        return maxB - maxA;
      });
    }
    return groups;
  }, [filteredData, activeTab]);

  const limit = 10;
  const totalPages = Math.max(1, Math.ceil(groupedFilteredData.length / limit));
  const safePage = Math.min(page, totalPages);
  const paginatedGroups = groupedFilteredData.slice((safePage - 1) * limit, safePage * limit);

  useEffect(() => { sessionStorage.setItem("pr_active_tab", activeTab); }, [activeTab]);

  // Fetch persisted docs list from the server whenever the Docs tab is
  // opened for a saved PR (has a real purchase_request_no / pl_number).
  useEffect(() => {
    const prNo = modalData?.purchase_request_no || modalData?.pl_number || "";
    if (createActiveTab === "docs" && prNo) {
      fetchUploadedDocs(prNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createActiveTab, modalData?.purchase_request_no, modalData?.pl_number]);
  useEffect(() => { setPage(1); }, [
    activeTab, selectedCategory, selectedSubCategory, selectedProductCode,
    selectedPurchaseRequestNo, selectedPurchaseOrganization, selectedStatus,
    selectedConstruction, selectedCode, selectedCableType, selectedCableCategory,
    selectedCores, selectedColor, selectedCrossSection, selectedStdRef, selectedVoltage
  ]);

  const selectableGroups = paginatedGroups.filter(g => g.items.some(item => !isFinalPR(item)));
  const approvedIds = (items) => items.filter(i => String(i.approval_status).toUpperCase() === "APPROVED").map(i => i.id);
  const isAllSelected = selectableGroups.length > 0 && selectableGroups.every(g => {
    const ids = approvedIds(g.items);
    return ids.length > 0 && ids.every(id => selectedItems.includes(id));
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedItems(selectableGroups.flatMap(g => approvedIds(g.items)));
    else setSelectedItems([]);
  };
  //eslint-disable-next-line
  const handleSelectItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  // Selecting a PR row selects/deselects every APPROVED product line under
  // that PR together — a line that's still Pending or was Rejected can't be
  // converted, so it's never added to selectedItems even when its PR row
  // checkbox is ticked. Only APPROVED lines flow into the Convert action.
  const handleSelectGroup = (items) => {
    const ids = items.filter(i => String(i.approval_status).toUpperCase() === "APPROVED").map(i => i.id);
    if (ids.length === 0) return;
    const allSelected = ids.every(id => selectedItems.includes(id));
    setSelectedItems(prev => allSelected
      ? prev.filter(id => !ids.includes(id))
      : [...new Set([...prev, ...ids])]
    );
  };

  const handleViewClick = async (productCode, prNo, status) => {
    try {
      const response = await axios.post(`${PURCHASE_API}/purchase-request/view`, {
        product_code: productCode,
        purchase_request_number: prNo
      }, { headers: authHeaders() });
      if (response.data.success) {
        const d = response.data.data;
        const procurementLeadTime = d.procurementLeadTime;
        const [procurementValue, procurementUnit] = procurementLeadTime
          ? procurementLeadTime.split(" ") : [null, null];

        setModalData({
          ...d,
          productDescription: d.productDescription || d.description || "",
          productCategory: d.productCategory || d.product_category || "",
          subCategory: d.subCategory || d.sub_category || "",
          productType: d.productType || d.product_type || "",
          product_code: d.product_code || productCode || "",
          purchase_organization: d.purchase_organization || "",
          purchase_planner: d.purchase_planner || "",
          location: d.location || "",
          // Existing PRs saved before this field was required (or rows the
          // user never touched) can come back with an empty requirement_date
          // — default those to today instead of leaving the picker blank.
          requirement_date: d.requirement_date || todayISO(),
          procurementLeadTime: {
            value: procurementValue ? parseInt(procurementValue) : "",
            unit: procurementUnit || ""
          },
          // IMPORTANT: prefer the FRESH status just returned by the view API
          // (d.status) over the `status` argument passed in from the list
          // row. The list row's value can be stale (e.g. it was CREATED
          // when the summary was last fetched, but has since been
          // submitted to Pending For Approval) — trusting it here caused
          // the modal to show CREATED-only controls (Save→submit) even
          // when the PR was already Pending For Approval, which blocked
          // both submit and approve.
          approval_status: d.status || status || "PENDING"
        });
        // Snapshot from the raw server response, before any local auto-fill
        // touches modalData — reflects what's actually saved in the DB.
        setCoreFieldsSavedAtOpen(!!(d.purchase_organization && d.purchase_planner && d.location));

        // Prefetch Characteristics mapping exactly to PR
        try {
          const pirRes = await axios.get(`${PURCHASE_API}/purchase-request/pir-characteristics`, {
        headers: authHeaders(),
            params: { product_code: productCode, purchase_request_no: prNo }
          });
          if (pirRes.data.success) {
            const fetchedChars = pirRes.data.characteristics || [];
            const fetchedPirNumber = pirRes.data.pir_number || null;
            setPirChars(fetchedChars);
            setPirCharsPirId(fetchedPirNumber);
            // Row 0's characteristics are read from modalData.pirChars /
            // modalData.pirNumber, so store them here too, not just in the
            // separate pirChars state.
            setModalData(prev => ({ ...prev, pirChars: fetchedChars, pirNumber: fetchedPirNumber }));
          }
        } catch (e) { console.error("Failed to load PR characteristics", e); }

        // Fetch every OTHER product line saved under this same PR number, so
        // multi-product PRs show all their products here too — not just the
        // single product that was clicked to open this modal.
        try {
          const linesRes = await axios.get(`${PURCHASE_API}/purchase-request/lines`, {
        headers: authHeaders(),
            params: { purchase_request_no: prNo }
          });
          if (linesRes.data?.success) {
            const siblingLines = (linesRes.data.data || []).filter(l =>
              d.id != null ? l.id !== d.id : l.product_code !== productCode
            );
            const siblingRows = await Promise.all(siblingLines.map(async (l) => {
              let linePirChars = [];
              let linePirNumber = l.pir_number || null;
              try {
                const charsRes = await axios.get(`${PURCHASE_API}/purchase-request/pir-characteristics`, {
        headers: authHeaders(),
                  params: { product_code: l.product_code, purchase_request_no: prNo }
                });
                if (charsRes.data.success) {
                  linePirChars = charsRes.data.characteristics || [];
                  linePirNumber = charsRes.data.pir_number || linePirNumber;
                }
              } catch (e) { /* ignore */ }

              return {
                _rowId: `line_${l.id}`,
                id: l.id,
                product_code: l.product_code || "",
                productDescription: l.description || "",
                uom: l.uom || "",
                hsnCode: l.hsnCode || "",
                special_remarks: l.special_remarks || "",
                requirement_qty: l.requirement_qty ?? "",
                requirement_date: l.requirement_date || todayISO(),
                productType: l.productType || "",
                productCategory: l.productCategory || "",
                subCategory: l.subCategory || "",
                product_class: l.product_class || "",
                // Each line item's OWN status, so partial approve/reject can be
                // shown/acted on per-row instead of assuming every product
                // under the PR shares row 0's status.
                approval_status: l.approval_status || "Pending For Approval",
                pirNumber: linePirNumber,
                pirChars: linePirChars
              };
            }));
            setExtraProducts(siblingRows);
          }
        } catch (e) { console.error("Failed to load sibling PR lines", e); }

        setShowModal(true);
        setCreateActiveTab("product");
        setReviewSelectedRows(new Set());
        setUploadedDocs([]);
        fetchLogs(prNo || d.pl_number || d.purchase_request_no);
        if (d.pl_number && (d.status || status) === "CREATED") {
          setIsEditMode(true);
        } else {
          setIsEditMode(false);
        }
      }
    } catch (err) { console.error("View Error:", err); }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalData(null);
    setModalFilterCategory(null);
    setModalFilterSubCategory(null);
    setReviewSelectedRows(new Set());
    setCommentModal(false);
    setComment("");
    setIsEditMode(false);
    setPirChars([]);
    setExtraProducts([]);
    setChangeLogs([]);
    setUploadedDocs([]);
    setShowPRDocInModal(false);
    setCoreFieldsSavedAtOpen(false);
    if (location.state?.returnTo) {
      navigate(location.state.returnTo, {
        state: { returnParams: location.state.returnParams }
      });
    }
  };

  // Actually calls the convert-to-PO endpoint. forcedSupplier is null for the
  // normal "same supplier" / "different suppliers → separate POs" path, or
  // { vendorCode, vendorName } when the user picked "Convert as One PO".
  const runConversion = (itemsWithQty, forcedSupplier) => {
    setIsConverting(true);
    axios.post(`${PURCHASE_API}/purchase-request/convert-to-purchase-order`, {
      selectedItems: itemsWithQty,
      created_by: currentUser,
      forced_vendor_code: forcedSupplier?.vendorCode || null,
      forced_vendor_name: forcedSupplier?.vendorName || null
    }, { headers: authHeaders() })
      .then(response => {
        if (response.data.success) {
          setConversionResult({
            message: response.data.message,
            data: response.data.data || [],
            total: itemsWithQty.length
          });
          setShowConversionModal(true);
          setSelectedItems([]);
          fetchSummary();

          // Fire-and-forget: calculate & store planned raw material cost for
          // each Purchase Order just created by this conversion. Doesn't block
          // the UI / conversion-result modal -- if this fails, it's logged only.
          (response.data.data || []).forEach(row => {
            if (!row.poId) return;
            axios.post(
              `${PURCHASE_API}/product-costing/calculate-planned-raw-material-cost`,
              { purchaseOrderNumber: row.poId },
              { headers: authHeaders() }
            ).catch(err => {
              console.error(`Failed to calculate planned cost for PO ${row.poId}:`, err);
            });
          });
        } else {
          toast.error(response.data.message);
        }
        setIsConverting(false);
      })
      .catch(error => {
        toast.error(error.response?.data?.message || "Error converting Purchase Request");
        setIsConverting(false);
      });
  };

  // Checks suppliers for every selected product code first. If they all
  // match, converts immediately (same as before). If they differ, opens a
  // dialog showing PR No / Product Code / Supplier for each selected item
  // and lets the user choose: convert as separate POs (one per supplier) or
  // pick a single supplier and merge everything into one PO.
  const handleConvertClick = async () => {
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one Purchase Request");
      return;
    }

    const selectedRows = selectedItems
      .map(id => productPlanningData.find(d => d.id === id))
      .filter(Boolean);

    const itemsWithQty = selectedRows.map(row => ({
  purchase_request_no: row.purchase_request_no,
  product_code: row.product_code,
  pr_qty: row.pr_qty || row.open_pr_qty || null
}));

    setIsCheckingSuppliers(true);
    try {
      const productCodes = [...new Set(selectedRows.map(r => r.product_code).filter(Boolean))];
      const supRes = await axios.post(`${PURCHASE_API}/purchase-request/suppliers-for-items`, {
        product_codes: productCodes
      }, { headers: authHeaders() });
      const supplierMap = supRes.data?.success ? supRes.data.data : {};

      const dialogItems = selectedRows.map(row => {
        const sup = supplierMap[row.product_code] || null;
        return {
          purchase_request_no: row.purchase_request_no,
          product_code: row.product_code,
          product_description: row.productDescription || row.description || "—",
          vendorCode: sup?.vendorCode || null,
          vendorName: sup?.vendorName || "Unknown Supplier"
        };
      });

      const distinctVendorKeys = [...new Set(dialogItems.map(i => i.vendorCode || i.vendorName))];

      if (distinctVendorKeys.length <= 1) {
        // Same supplier across every selected item — convert straight away.
        runConversion(itemsWithQty, null);
      } else {
        // Different suppliers — ask the user how to proceed.
        const distinctOptions = [];
        const seen = new Set();
        dialogItems.forEach(i => {
          const key = i.vendorCode || i.vendorName;
          if (!seen.has(key)) {
            seen.add(key);
            distinctOptions.push({ vendorCode: i.vendorCode, vendorName: i.vendorName });
          }
        });

        setSupplierDialogItems(dialogItems);
        setSupplierDialogOptions(distinctOptions);
        setSelectedForcedSupplier(null);
        setPendingConvertItems(itemsWithQty);
        setShowSupplierDialog(true);
      }
    } catch (err) {
      console.error("Supplier check failed:", err);
      toast.error("Could not verify suppliers for the selected items");
    } finally {
      setIsCheckingSuppliers(false);
    }
  };

  const closeSupplierDialog = () => {
    setShowSupplierDialog(false);
    setSupplierDialogItems([]);
    setSupplierDialogOptions([]);
    setSelectedForcedSupplier(null);
    setPendingConvertItems([]);
  };

  // "Continue with Different Suppliers" — backend groups items by their own
  // default supplier, so each distinct supplier gets its own PO.
  const handleContinueDifferentSuppliers = () => {
    runConversion(pendingConvertItems, null);
    closeSupplierDialog();
  };

  // "Convert as One PO" — every selected item is forced under the one
  // supplier picked in the dropdown.
  const handleConvertAsOnePO = () => {
    if (!selectedForcedSupplier) {
      toast.warning("Please select a supplier to convert as one PO");
      return;
    }
    runConversion(pendingConvertItems, selectedForcedSupplier);
    closeSupplierDialog();
  };

  const closeConversionModal = () => {
    setShowConversionModal(false);
    setConversionResult(null);
  };

  const handleSave = async () => {
    // Planning Element PR (any PR with a linked Sales Order routes here,
    // since the backend's view query always sets pl_number for those).
    //
    // FIX: this only ever validated/sent modalData.pr_qty — a field that's
    // not an input on this basic edit screen at all (it's used by the
    // separate handleSaveAndResubmit flow) and is always blank here. The
    // Qty box actually on screen is bound to modalData.requirement_qty, and
    // it was never included in the payload — this is the same class of bug
    // as the purchase_request_no branch below, just in the branch that
    // Sales-Order-linked PRs actually go through. Now sends product_code +
    // qty here too, alongside the pre-existing pr_qty check (kept in case
    // it's ever populated by another flow sharing this Save button).
    if (modalData.pl_number) {
      const prQtyVal = parseFloat(modalData.pr_qty);
      if (modalData.pr_qty !== "" && modalData.pr_qty !== undefined && modalData.pr_qty !== null) {
        if (isNaN(prQtyVal) || prQtyVal <= 0) {
          toast.error("PR Qty must be a positive number.");
          return false;
        }
      }
      if (modalData.requirement_qty !== "" && modalData.requirement_qty !== undefined && modalData.requirement_qty !== null) {
        const qtyVal = parseFloat(modalData.requirement_qty);
        if (isNaN(qtyVal) || qtyVal <= 0) {
          toast.error("Qty must be a positive number.");
          return false;
        }
      }

      try {
        const res = await axios.post(`${PURCHASE_API}/purchase-request/save-details`, {
          purchase_request_no: modalData.pl_number,
          purchase_organization: modalData.purchase_organization,
          purchase_planner: modalData.purchase_planner,
          location: modalData.location,
          requirement_date: modalData.requirement_date || todayISO(),
          product_code: modalData.product_code,
          qty: modalData.requirement_qty,
          special_remarks: modalData.special_remarks || "",
          created_by: currentUser,
        }, { headers: authHeaders() });
        if (!res.data.success) { toast.error(res.data.message); return false; }

        toast.success(res.data.message);
        fetchSummary();
        closeModal();
        return true;
      } catch (err) {
        toast.error("Save Failed");
        return false;
      }
    }

    // Already-existing manually-created PR being edited (has
    // purchase_request_no, no pl_number) — update in place, don't recreate.
    //
    // FIX: this previously only validated/sent modalData.pr_qty, a field
    // that's never populated on this edit modal (it's set only on the
    // separate Convert-to-PO list). The Qty box the user actually types
    // into is bound to modalData.requirement_qty (row 0) and each
    // extraProducts row's own requirement_qty, and neither was ever
    // included in the save payload — so quantity edits silently never
    // reached the backend. Now: save each line's own product_code + qty.
    if (modalData.purchase_request_no) {
      const linesToSave = [
        { product_code: modalData.product_code, qty: modalData.requirement_qty, special_remarks: modalData.special_remarks || "" },
        ...extraProducts.map(r => ({ product_code: r.product_code, qty: r.requirement_qty, special_remarks: r.special_remarks || "" }))
      ].filter(l => l.product_code); // skip any unfilled/blank row

      for (const line of linesToSave) {
        if (line.qty !== "" && line.qty !== undefined && line.qty !== null) {
          const qtyVal = parseFloat(line.qty);
          if (isNaN(qtyVal) || qtyVal <= 0) {
            toast.error(`Qty for ${line.product_code} must be a positive number.`);
            return false;
          }
        }
      }

      try {
        for (const line of linesToSave) {
          const res = await axios.post(`${PURCHASE_API}/purchase-request/save-details`, {
            purchase_request_no: modalData.purchase_request_no,
            purchase_organization: modalData.purchase_organization,
            purchase_planner: modalData.purchase_planner,
            location: modalData.location,
            requirement_date: modalData.requirement_date || todayISO(),
            product_code: line.product_code,
            qty: line.qty,
            special_remarks: line.special_remarks || "",
            created_by: currentUser,
          }, { headers: authHeaders() });
          if (!res.data.success) { toast.error(res.data.message); return false; }
        }

        toast.success("Saved successfully");
        fetchSummary();
        closeModal();
        return true;
      } catch (err) {
        toast.error("Save Failed");
        return false;
      }
    }

    // Manual Create PR — row 0 (modalData) + any additional rows added via "+ Add Product"
    if (!modalData.requirement_date) modalData.requirement_date = todayISO();
    if (!modalData.product_code || !modalData.requirement_qty) {
      toast.error("Product Code and Quantity are required.");
      return false;
    }
    for (const row of extraProducts) {
      if (!row.requirement_date) row.requirement_date = todayISO();
      if (!row.product_code || !row.requirement_qty) {
        toast.error("Please complete Product Code and Quantity for every product row.");
        return false;
      }
    }

    const toLine = (r) => ({
      product_code: r.product_code,
      qty: r.requirement_qty,
      requirement_date: r.requirement_date,
      uom: r.uom || "",
      hsn_code: r.hsnCode || "",
      special_remarks: r.special_remarks || "",
      productType: r.productType || "",
      productCategory: r.productCategory || "",
      sub_category: r.subCategory || "",
      product_class: r.product_class || "",
      description: r.productDescription || r.description || ""
    });

    const payload = {
      products: [toLine(modalData), ...extraProducts.map(toLine)],
      purchase_organization: modalData.purchase_organization,
      purchase_planner: modalData.purchase_planner,
      location: modalData.location,
      created_by: currentUser,
      // legacy single-product fields kept for backward compatibility
      ...toLine(modalData)
    };

    try {
      const res = await axios.post(`${PURCHASE_API}/purchase-request/create-pr`, payload, { headers: authHeaders() });
      if (res.data.success) {
        const newPrNo = res.data.data?.purchase_request_no;

        // Immediately submit the freshly-created PR so it goes straight to
        // "Pending For Approval" instead of sitting in CREATED status
        // waiting for a manual Submit click.
        let finalStatus = "CREATED";
        if (newPrNo) {
          try {
            const submitRes = await axios.post(`${PURCHASE_API}/purchase-request/submit-pr`, {
              purchase_request_no: newPrNo
            }, { headers: authHeaders() });
            if (submitRes.data.success) {
              finalStatus = "Pending For Approval";
            } else {
              toast.warning(submitRes.data.message || "PR created but could not be auto-submitted.");
            }
          } catch (submitErr) {
            toast.warning("PR created but could not be auto-submitted for approval.");
          }
        }

        toast.success(res.data.message);
        fetchSummary();
        // Keep the modal open and jump straight to the Review tab so the
        // user can see everything that was just created (and submitted).
        setModalData(prev => ({
          ...prev,
          purchase_request_no: newPrNo || prev.purchase_request_no,
          approval_status: finalStatus
        }));
        setIsEditMode(false);
        setCreateActiveTab("review");
        return true;
      } else {
        toast.error(res.data.message);
        return false;
      }
    } catch (err) {
      toast.error("Save Failed");
      return false;
    }
  };

  const handleSaveAndResubmit = async () => {
    const openCeiling = parseFloat(modalData.open_pr_qty ?? modalData.requirement_qty) || 0;
    const prQtyVal = parseFloat(modalData.pr_qty);
    if (modalData.pr_qty !== "" && modalData.pr_qty !== undefined && modalData.pr_qty !== null) {
      if (isNaN(prQtyVal) || prQtyVal <= 0) { toast.error("PR Qty must be a positive number."); return; }
      if (prQtyVal > openCeiling) { toast.error(`PR Qty (${prQtyVal}) cannot exceed Open PR Qty (${openCeiling}).`); return; }
    }

    const prNo = modalData.pl_number || modalData.purchase_request_no;

    // FIX: this used to post save-details with NO product_code and NO qty,
    // so once a PR had been submitted (status != CREATED) and was edited
    // again, the backend only updated org/planner/location/date — the qty
    // typed into the box never reached the DB. (The first save worked only
    // because a CREATED PR goes through handleSubmitPR, which does send qty.)
    // Now every line on screen (row 0 + extraProducts) is saved with its own
    // product_code + qty + requirement_date, same as handleSave.
    const linesToSave = [
      { product_code: modalData.product_code, qty: modalData.requirement_qty, requirement_date: modalData.requirement_date, special_remarks: modalData.special_remarks || "" },
      ...extraProducts.map(r => ({ product_code: r.product_code, qty: r.requirement_qty, requirement_date: r.requirement_date, special_remarks: r.special_remarks || "" }))
    ].filter(l => l.product_code);

    for (const line of linesToSave) {
      if (line.qty !== "" && line.qty !== undefined && line.qty !== null) {
        const qtyVal = parseFloat(line.qty);
        if (isNaN(qtyVal) || qtyVal <= 0) {
          toast.error(`Qty for ${line.product_code} must be a positive number.`);
          return;
        }
      }
    }

    try {
      // Safety net: no product lines resolved -> header-only save as before.
      const payloads = linesToSave.length
        ? linesToSave
        : [{ product_code: undefined, qty: undefined, requirement_date: modalData.requirement_date }];

      for (const line of payloads) {
        const saveRes = await axios.post(`${PURCHASE_API}/purchase-request/save-details`, {
          purchase_request_no: prNo,
          purchase_organization: modalData.purchase_organization,
          purchase_planner: modalData.purchase_planner,
          location: modalData.location,
          requirement_date: line.requirement_date || modalData.requirement_date || todayISO(),
          product_code: line.product_code,
          qty: line.qty,
          special_remarks: line.special_remarks || "",
          reset_status: true,
          created_by: currentUser,
        }, { headers: authHeaders() });
        if (!saveRes.data.success) { toast.error(saveRes.data.message || "Save failed."); return; }
      }

      const submitRes = await axios.post(`${PURCHASE_API}/purchase-request/submit-pr`, {
        purchase_request_no: prNo
      }, { headers: authHeaders() });
      if (submitRes.data.success) {
        toast.success("Changes saved. PR is now Pending For Approval.");
        setIsEditMode(false);
        fetchSummary();
        closeModal();
      } else {
        toast.error(submitRes.data.message);
      }
    } catch (err) {
      toast.error("Save failed.");
    }
  };

  const handleApprove = () => {
    if (reviewSelectedRows.size === 0) { toast.warning("Please select the item(s) to approve"); return; }
    setActionType("APPROVED"); setCommentModal(true);
  };

  const handleSubmitPR = async () => {
    let prNo = modalData.purchase_request_no || modalData.pl_number;

    if (modalData.pl_number) {
      try {
        const saveRes = await axios.post(`${PURCHASE_API}/purchase-request/save-details`, {
          purchase_request_no: modalData.pl_number,
          purchase_organization: modalData.purchase_organization,
          purchase_planner: modalData.purchase_planner,
          location: modalData.location,
          requirement_date: modalData.requirement_date || todayISO(),
          product_code: modalData.product_code,
          qty: modalData.requirement_qty,
          special_remarks: modalData.special_remarks || "",
          created_by: currentUser,
        }, { headers: authHeaders() });
        if (!saveRes.data.success) { toast.error(saveRes.data.message || "Save failed before submit."); return; }
      } catch (err) {
        toast.error("Failed to save details before submitting."); return;
      }
    } else if (!prNo) {
      // Brand-new manually-created PR that hasn't been saved to the DB yet — create it first.
      if (!modalData.requirement_date) modalData.requirement_date = todayISO();
      if (!modalData.product_code || !modalData.requirement_qty) {
        toast.error("Please select a Product and Qty before submitting.");
        return;
      }
      for (const row of extraProducts) {
        if (!row.requirement_date) row.requirement_date = todayISO();
        if (!row.product_code || !row.requirement_qty) {
          toast.error("Please complete Product Code and Qty for every product row before submitting.");
          return;
        }
      }
      try {
        const toLine = (r) => ({
          product_code: r.product_code,
          qty: r.requirement_qty,
          requirement_date: r.requirement_date,
          uom: r.uom || "",
          hsn_code: r.hsnCode || "",
          special_remarks: r.special_remarks || "",
          productType: r.productType || "",
          productCategory: r.productCategory || "",
          sub_category: r.subCategory || "",
          product_class: r.product_class || "",
          description: r.productDescription || r.description || ""
        });

        const createRes = await axios.post(`${PURCHASE_API}/purchase-request/create-pr`, {
          products: [toLine(modalData), ...extraProducts.map(toLine)],
          purchase_organization: modalData.purchase_organization,
          purchase_planner: modalData.purchase_planner,
          location: modalData.location,
          created_by: currentUser,
          // legacy single-product fields kept for backward compatibility
          ...toLine(modalData)
        }, { headers: authHeaders() });
        if (!createRes.data.success) { toast.error(createRes.data.message || "Failed to create PR before submit."); return; }
        prNo = createRes.data.purchase_request_no || createRes.data.data?.purchase_request_no;
        if (!prNo) { toast.error("PR created but number missing — please try Save instead."); return; }
      } catch (err) {
        toast.error("Failed to create PR before submitting."); return;
      }
    }

    try {
      const res = await axios.post(`${PURCHASE_API}/purchase-request/submit-pr`, { purchase_request_no: prNo }, { headers: authHeaders() });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchSummary();
        closeModal();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) { toast.error("Submit Failed"); }
  };

  const handleReject = () => {
    if (reviewSelectedRows.size === 0) { toast.warning("Please select the item(s) to reject"); return; }
    setActionType("REJECTED"); setCommentModal(true);
  };
 
  const handleReviewSubmit = () => {
    if (actionType === "REJECTED" && !comment.trim()) { toast.warning("Comment is mandatory for rejection"); return; }

    // Map the selected row keys ("row0" or a sibling's _rowId) to their
    // actual DB row ids, so only those specific line items get actioned —
    // the rest of the PR's products stay Pending For Approval untouched.
    // Row id (not product_code) is required here because a single PR can
    // have two lines with the SAME product_code (different qty/date) — an
    // action scoped by product_code alone would match/act on both.
    const selectedRowIds = [];
    const selectedProductCodes = []; // kept as a fallback for legacy rows with no id yet
    if (reviewSelectedRows.has("row0") && modalData.product_code) {
      if (modalData.id != null) selectedRowIds.push(modalData.id);
      else selectedProductCodes.push(modalData.product_code);
    }
    extraProducts.forEach(row => {
      if (reviewSelectedRows.has(row._rowId) && row.product_code) {
        if (row.id != null) selectedRowIds.push(row.id);
        else selectedProductCodes.push(row.product_code);
      }
    });

    const payload = {
      purchase_request_no: modalData.pl_number || modalData.purchase_request_no,
      purchase_organization: modalData.purchase_organization,
      purchase_planner: modalData.purchase_planner,
      location: modalData.location,
      action_status: actionType,
      approver_id: getCurrentUser(),
      remarks: comment.trim(),
      row_ids: selectedRowIds,
      product_codes: selectedProductCodes
    };
    axios.post(`${PURCHASE_API}/purchase-request/action`, payload, { headers: authHeaders() })
      .then(res => {
        if (res.data.success) { toast.success(res.data.message); setCommentModal(false); setComment(""); closeModal(); fetchSummary(); }
        else toast.error(res.data.message);
      })
      .catch(() => toast.error("Error processing request"));
  };

  const getTodayDate = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getTodayDateDisplay = () => {
    const [yyyy, mm, dd] = getTodayDate().split("-");
    return `${dd}/${mm}/${yyyy}`;
  };

  // ── Insert-row helpers (the "+" icon on each product row) ──
  const makeBlankPRRow = () => ({
    _rowId: `row_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    product_code: "", productDescription: "", uom: "",
    hsnCode: "", special_remarks: "", requirement_qty: "", requirement_date: todayISO(),
    productType: "", productCategory: "", subCategory: "", product_class: "",
    pirNumber: null, pirChars: []
  });
  // afterIdx: 0 = insert below row 1 (modalData); n = insert below extraProducts[n-1]
  const insertPRRowAfter = (afterIdx) =>
    setExtraProducts(prev => {
      const next = [...prev];
      next.splice(afterIdx, 0, makeBlankPRRow());
      return next;
    });

  const todayISO = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Fixed dd/mm/yyyy hh:mm:ss formatter, always in Indian Standard Time
  // (Asia/Kolkata), regardless of the viewer's device timezone/locale or the
  // UTC value sent by the server. Used for Change Log / Docs "Date & Time".
  const formatChangeLogDateTime = (dateInput) => {
    if (!dateInput) return "-";
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(d.getTime())) return "-";
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const hh = parts.hour === '24' ? '00' : parts.hour;
    return `${parts.day}/${parts.month}/${parts.year} ${hh}:${parts.minute}:${parts.second}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const s = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // Full UTC/offset timestamps from the server (e.g. created_at) -> take the
    // calendar date as seen in IST, so the day never shifts on non-IST devices.
    if (/^\d{4}-\d{2}-\d{2}T.*(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
      const t = new Date(s);
      if (!isNaN(t.getTime())) {
        const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(t);
        if (/^\d{4}-\d{2}-\d{2}$/.test(p)) return p;
      }
    }
    const d = new Date(s);
    if (isNaN(d.getTime())) return s.substring(0, 10);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Display-only formatter: dd/mm/yyyy (use everywhere except <input type="date">, which needs yyyy-mm-dd)
  const formatDateDisplay = (dateStr) => {
    const iso = formatDate(dateStr);
    if (!iso) return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    const [, yyyy, mm, dd] = m;
    return `${dd}/${mm}/${yyyy}`;
  };

  // True only for a brand-new manually-created PR (no existing PR/planning-element number yet)
  const isCreateMode = !!modalData && !modalData.pl_number && !modalData.purchase_request_no;

  // True while a planning-origin PR hasn't actually been saved yet — opened
  // in edit mode, still CREATED status, and Purchase Organization/Planner/
  // Location weren't already persisted on the backend when the modal was
  // opened (coreFieldsSavedAtOpen). Deliberately does NOT look at the live
  // modalData values, since those get auto-filled locally as soon as a
  // dropdown has a single option — that auto-fill shouldn't by itself
  // unlock the Docs tab; only an actual Save (persisting to the DB) should.
  const isUnsavedPlanningDraft = !!modalData && isEditMode && modalData.approval_status === "CREATED" &&
    !coreFieldsSavedAtOpen;

  const selectStyles = {
    control: base => ({ ...base, minHeight: 32, height: 32, fontSize: '13px', borderColor: '#dee2e6', boxShadow: 'none' }),
    valueContainer: base => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: base => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: base => ({ ...base, padding: 4 }),
    menu: base => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
  };

  return (
    <Layout>
      <style>{`
        .pr-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; }
        .pr-tab-toggle button { border: none; background: transparent; border-radius: 6px; padding: 5px 20px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.15s ease; display: flex; align-items: center; gap: 6px; }
        .pr-tab-toggle button.pr-active-tab { background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,0.12); }
        .pr-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; border-radius: 9px; font-size: 11px; font-weight: 700; padding: 0 5px; line-height: 1; }
        .pr-badge-active { background: #dbeafe; color: #1d4ed8; }
        .pr-badge-posted { background: #dcfce7; color: #15803d; }
      `}</style>

      {/* ── Page Title ── */}
      <div style={styles.pageTitleBar}>
        <div>
          <h2 style={styles.pageTitle}>Purchase Requisition</h2>
          <div style={styles.pageSubtitle}>Manage, approve and convert purchase requisitions</div>
        </div>
        {activeTab === "active" && (
          <div className="d-flex gap-2">
            {canCreate && (
              <button className="btn btn-dark" onClick={() => {
                setModalData({ productType: "", productCategory: "", subCategory: "", product_code: "", requirement_qty: "", open_pr_qty: "", pr_qty: "", requirement_date: todayISO(), hsnCode: "", special_remarks: "", productDescription: "", uom: "", purchase_organization: "SB01" });
                setModalFilterCategory(null);
                setModalFilterSubCategory(null);
                setPirChars([]);
                setExtraProducts([]);
                setCreateActiveTab("product");
                setShowModal(true); setIsEditMode(true);
              }}>
                Create
              </button>
            )}
            {canConvert && (
              <button className="btn btn-dark" style={styles.primaryBtn}
                disabled={isConverting || isCheckingSuppliers || selectedItems.length === 0} onClick={handleConvertClick}>
                {isConverting ? "Converting..." : isCheckingSuppliers ? "Checking suppliers..." : "Convert"}
              </button>
            )}
            <button
              onClick={() => {
                setManualExists(null);
                setManualFile(null);
                setShowManual(true);
                fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                  .then((res) => res.json())
                  .then((data) => {
                    const norm = (v) => String(v || "").trim().toLowerCase();
                    const match = (data?.data || []).find((m) =>
                      norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                      norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                      norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                    );
                    if (match && match.drive_link) {
                      setManualFile(match);
                      setManualExists(true);
                    } else {
                      setManualExists(false);
                    }
                  })
                  .catch(() => setManualExists(false));
              }}
      title="View User Manual"
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        color: "#3b82f6",
        fontSize: "14px",
        fontWeight: "600",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
    >
      <FileText size={18} />
      User Manual
    </button>
          </div>
        )}
      </div>

      {showManual && (
  <div
    onClick={() => setShowManual(false)}
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1100,
    }}
  >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              width: "90vw",
              height: "97vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
            }}
          >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 20px",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
          User Manual
        </h4>
        <button
          onClick={() => setShowManual(false)}
          style={{
            border: "none",
            background: "#f1f5f9",
            borderRadius: "8px",
            width: "32px",
            height: "32px",
            cursor: "pointer",
            fontSize: "18px",
            color: "#475569",
            lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {manualExists === null && (
        <div style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          color: "#94a3b8", fontSize: "14px"
        }}>
          Checking for user manual...
        </div>
      )}

      {manualExists === false && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
        }}>
          <FileText size={40} color="#cbd5e1" />
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
            User Manual Not Available
          </div>
          <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
            The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
          </div>
        </div>
      )}

      {manualExists === true && manualFile && (() => {
    const MANUAL_SCALE = 1.25;
    return (
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
            <iframe
                src={manualFile.drive_link.replace("/view", "/preview")}
                title={manualFile.file_name || "User Manual"}
                allow="autoplay"
                style={{
                    border: "none",
                    width: `${100 / MANUAL_SCALE}%`,
                    height: `${100 / MANUAL_SCALE}%`,
                    transform: `scale(${MANUAL_SCALE})`,
                    transformOrigin: "top left",
                }}
            />
        </div>
    );
})()}
    </div>
  </div>
)}

      {/* CREATE-only roles (canView false) never see the list — same
          "add-only, no visibility" rule used for Enquiry. */}
      {!canView ? (
        <div style={{ ...styles.filterCard, textAlign: "center", color: "#94a3b8", padding: "40px 20px" }}>
          {canCreate
            ? "You can create new purchase requisitions, but you don't have access to view the existing list."
            : "You don't have access to view purchase requisitions."}
        </div>
      ) : (
      <>
      {showExpiredOnly && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
          padding: "10px 16px", marginBottom: 16, color: "#991b1b", fontWeight: 500
        }}>
          <span>Showing Purchase Requisitions with expired validity only</span>
          <button
            onClick={() => setShowExpiredOnly(false)}
            style={{
              background: "transparent", border: "1px solid #991b1b", color: "#991b1b",
              borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 13
            }}
          >
            Clear
          </button>
        </div>
      )}
      {!showExpiredOnly && (
      <>
      {/* ── Filters ── */}
      <div style={styles.filterCard}>
        <div className="row g-2 mb-2">
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>Product Type</label>
            <Select
              options={productTypeOptions}
              value={selectedProductType}
              onChange={(opt) => {
                // Changing Product Type invalidates whatever code was picked
                // under the previous type (product codes vs asset codes are
                // different tables), so clear it same as the modal does.
                setSelectedProductType(opt);
                setSelectedProductCode(null);
                // Cable attribute options are scoped to the Product Type, so
                // drop any attribute picked under the previous type.
                setSelectedConstruction(null); setSelectedCode(null); setSelectedCableType(null);
                setSelectedCableCategory(null); setSelectedCores(null); setSelectedColor(null);
                setSelectedCrossSection(null); setSelectedStdRef(null); setSelectedVoltage(null); setSelectedCategory(null); setSelectedSubCategory(null);
              }}
              isClearable
              placeholder="Select"
              styles={selectStyles}
            />
          </div>
        </div>

        {/* ── Cable attribute filters — 2nd row, matches Stock Report layout ── */}
        {selectedProductType?.value !== "Asset" && hasCableAttrs && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(9, minmax(90px, 1fr))", gap: 10, marginBottom: 12 }}>
          <div>
            <label style={styles.filterLabel}>Construction</label>
            <Select options={constructionOptions} value={selectedConstruction} onChange={setSelectedConstruction} isClearable placeholder="Construction" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
          <div>
            <label style={styles.filterLabel}>Code</label>
            <Select options={codeOptions} value={selectedCode} onChange={setSelectedCode} isClearable placeholder="Code" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
          <div>
            <label style={styles.filterLabel}>Cable Type</label>
            <Select options={cableTypeOptions} value={selectedCableType} onChange={setSelectedCableType} isClearable placeholder="Cable Type" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
          <div>
            <label style={styles.filterLabel}>Category</label>
            <Select options={cableCategoryOptions} value={selectedCableCategory} onChange={setSelectedCableCategory} isClearable placeholder="Category" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
          <div>
            <label style={styles.filterLabel}>Cores</label>
            <Select options={coresOptions} value={selectedCores} onChange={setSelectedCores} isClearable placeholder="Cores" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
          <div>
            <label style={styles.filterLabel}>Color</label>
            <Select options={colorOptions} value={selectedColor} onChange={setSelectedColor} isClearable placeholder="Color" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
          <div>
            <label style={styles.filterLabel}>Cross Section</label>
            <Select options={crossSectionOptions} value={selectedCrossSection} onChange={setSelectedCrossSection} isClearable placeholder="Cross Section" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
          <div>
            <label style={styles.filterLabel}>Std. Ref</label>
            <Select options={stdRefOptions} value={selectedStdRef} onChange={setSelectedStdRef} isClearable placeholder="Std. Ref" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
          <div>
            <label style={styles.filterLabel}>Voltage</label>
            <Select options={voltageOptions} value={selectedVoltage} onChange={setSelectedVoltage} isClearable placeholder="Voltage" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
        </div>
        )}

        {/* ── Raw Material / non-cable types: Category -> Sub Category (cascading) ── */}
        {!(selectedProductType?.value !== "Asset" && hasCableAttrs) && (
        <div className="row g-2 mb-2">
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>Category</label>
            <Select
              options={rmCategoryOptions}
              value={selectedCategory}
              onChange={(opt) => { setSelectedCategory(opt); setSelectedSubCategory(null); }}
              isClearable
              placeholder="Category"
              styles={selectStyles}
            />
          </div>
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>Sub Category</label>
            <Select
              options={rmSubCategoryOptions}
              value={selectedSubCategory}
              onChange={setSelectedSubCategory}
              isClearable
              placeholder="Sub Category"
              styles={selectStyles}
            />
          </div>
        </div>
        )}

        <div className="row g-2">
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>{selectedProductType?.value === 'Asset' ? 'Asset Code' : 'Product Code'}</label>
            <Select options={listFilterCodeOptions} value={selectedProductCode} onChange={setSelectedProductCode} isClearable placeholder="Select" styles={selectStyles} />
          </div>
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>Purchase Request</label>
            <Select options={purchaseRequestOptions} value={selectedPurchaseRequestNo} onChange={setSelectedPurchaseRequestNo} isClearable placeholder="Select" styles={selectStyles} />
          </div>
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>Status</label>
            <Select options={statusFilterOptions} value={selectedStatus} onChange={setSelectedStatus} isClearable placeholder="Select" styles={selectStyles} />
          </div>
        </div>
      </div>
      </>
      )}

      {/* ── Active / Posted Toggle ── */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <div className="pr-tab-toggle">
          <button className={activeTab === "active" ? "pr-active-tab" : ""} onClick={() => setActiveTab("active")}>
            Active {activeCount > 0 && <span className="pr-badge pr-badge-active">{activeCount}</span>}
          </button>
          <button className={activeTab === "posted" ? "pr-active-tab" : ""} onClick={() => setActiveTab("posted")}>
            Posted {postedCount > 0 && <span className="pr-badge pr-badge-posted">{postedCount}</span>}
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={styles.tableContainer}>
        <div style={{ width: "100%", overflowX: "hidden" }}>
          <table className="table" style={styles.erpTable}>
            <thead>
              <tr>
                {activeTab === "active" && (
                  <th style={{ ...styles.erpTh, width: 50, textAlign: "center" }}>
                    <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                  </th>
                )}
                <th style={{ ...styles.erpTh, width: 180 }}>Purchase Request</th>
                {activeTab === "posted" && <th style={{ ...styles.erpTh, width: 180 }}>PO Number</th>}
                <th style={{ ...styles.erpTh, width: 140 }}>Product Code</th>
                <th style={{ ...styles.erpTh, width: 220 }}>Description</th>
                <th style={{ ...styles.erpTh, width: 140 }}>Requirement Date</th>
                <th style={{ ...styles.erpTh, width: 140 }}>Availability Date</th>
                <th style={{ ...styles.erpTh, width: 140 }}>Created Date</th>
                <th style={{ ...styles.erpTh, width: 170 }}>Status</th>
                <th style={{ ...styles.erpTh, width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {paginatedGroups.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center p-4 text-muted">
                    {activeTab === "posted" ? "No converted purchase requisitions yet." : "No records found."}
                  </td>
                </tr>
              ) : paginatedGroups.map((group, index) => {
                const prNo = group.purchase_request_no;
                const items = group.items;
                const first = items[0];
                const isMulti = items.length > 1;
                const isConvertedToPO = first.pr_status === 'CONVERTED';
                const isFinal = isFinalPR(first);
                const groupApprovedIds = items.filter(i => String(i.approval_status).toUpperCase() === "APPROVED").map(i => i.id);
                const groupSelected = groupApprovedIds.length > 0 && groupApprovedIds.every(id => selectedItems.includes(id));
                const uniqueDates = [...new Set(items.map(item => item.requirement_date))];
                const uniqueStatuses = [...new Set(items.map(item => item.approval_status))];
                const isExpired = expiredKeys.has(prNo);

                // Posting state for the whole PR: every line converted to a PO =
                // POSTED (green), none converted = NOT_POSTED (red), else PARTIAL (yellow).
                const lineState = (it) => {
                  const req  = Number(it.requirement_qty) || 0;
                  const open = it.open_pr_qty == null ? req : Number(it.open_pr_qty) || 0;
                  if (it.pr_status === 'CONVERTED' || (req > 0 && open <= 0)) return 2;
                  if (open < req) return 1;
                  return 0;
                };
                const lineStates = items.map(lineState);
                const postingState = lineStates.every(v => v === 2) ? "POSTED"
                  : lineStates.every(v => v === 0) ? "NOT_POSTED" : "PARTIAL";

                // Bootstrap's .table class paints background-color on each
                // <td>, not the <tr> — so setting it only on <tr> gets
                // painted over (same fix as the RFQ/Quotation and Sales
                // Order pages). Merge the row's background into every cell's
                // style instead; expired validity wins over the other row
                // background states.
                const rowCellStyle = {
                  ...styles.erpTd,
                  backgroundColor: isExpired
                    ? "#fde8e8"
                    : (groupSelected ? "#eff6ff" : POSTING_ROW_BG[postingState])
                };

                return (
                  <React.Fragment key={prNo || first.id}>
                    <tr style={{ opacity: isFinal && activeTab === "active" ? 0.6 : 1 }}>
                      {activeTab === "active" && (
                        <td style={{ ...rowCellStyle, textAlign: "center" }}>
                          {isConvertedToPO
                            ? <span style={{ color: "#16a34a", fontSize: 14 }} title="Already converted to PO">✓</span>
                            : <input
                                type="checkbox"
                                checked={groupSelected}
                                disabled={groupApprovedIds.length === 0}
                                title={groupApprovedIds.length === 0 ? "No approved lines to convert yet" : ""}
                                onChange={() => handleSelectGroup(items)}
                                style={{ cursor: groupApprovedIds.length === 0 ? "not-allowed" : "pointer" }}
                              />
                          }
                        </td>
                      )}
                      <td style={{ ...rowCellStyle, fontWeight: 600 }}>
                        <button onClick={() => handleViewClick(first.product_code, prNo, first.approval_status)} style={styles.prLink}>
                          {prNo}
                        </button>
                      </td>
                      {activeTab === "posted" && (
                        <td style={rowCellStyle}>
                          {first.purchase_order_id
                            ? (
                              <button
                                style={styles.prLink}
                                onClick={() => navigate('/cable/procurement/purchase-order', { state: { openPoNo: first.purchase_order_id } })}
                              >
                                {first.purchase_order_id}
                              </button>
                            )
                            : "-"}
                        </td>
                      )}
                      <td style={rowCellStyle}>
                        {items.map((it, i) => (
                          <div key={it.id ?? i} style={i > 0 ? { marginTop: 4, paddingTop: 4, borderTop: "1px dashed #f1f5f9" } : undefined}>
                            {it.product_code || "—"}
                          </div>
                        ))}
                      </td>
                      <td style={rowCellStyle}>
                        {items.map((it, i) => (
                          <div key={it.id ?? i} style={i > 0 ? { marginTop: 4, paddingTop: 4, borderTop: "1px dashed #f1f5f9" } : undefined}>
                            {it.productDescription || it.description || "—"}
                          </div>
                        ))}
                      </td>
                      <td style={rowCellStyle}>{isMulti ? (uniqueDates.length === 1 ? formatDateDisplay(uniqueDates[0]) : "Multiple") : formatDateDisplay(first.requirement_date)}</td>
                      <td style={rowCellStyle}>{isMulti ? "—" : formatDateDisplay(first.availability_date)}</td>
                      <td style={{ ...rowCellStyle, whiteSpace: "nowrap" }}>{formatDateDisplay(first.created_at)}</td>
                      <td style={{ ...rowCellStyle, whiteSpace: "nowrap" }}>
                        {isMulti
                          ? (uniqueStatuses.length === 1
                              ? uniqueStatuses[0]
                              : (uniqueStatuses.some(s => String(s).toUpperCase() === "APPROVED")
                                  ? "Partially Approved"
                                  : uniqueStatuses.some(s => String(s).toUpperCase() === "REJECTED")
                                    ? "Partially Rejected"
                                    : "Partially Approved"))
                          : first.approval_status}
                      </td>
                      <td style={rowCellStyle}>
                        <button className="btn btn-outline-dark btn-sm" style={{ fontSize: "11px", padding: "2px 8px" }}
                          onClick={() => handleViewClick(first.product_code, prNo, first.approval_status)}>
                          View
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between pt-3">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Page <b>{safePage}</b> of <b>{totalPages}</b> &bull; Total {groupedFilteredData.length}
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <button className="btn btn-outline-secondary btn-sm" disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      </div>
      </>
      )}

      {/* ── View / Create Modal ── (renders regardless of canView so a
          create-only user can still reach the Create form) */}
      {showModal && modalData && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: "1200px", width: "90%", maxHeight: "90vh", fontSize: "14px" }}>
            <div style={{ ...styles.modalHeader, padding: "10px 14px" }}>
              <div style={{ fontWeight: 600, fontSize: "18px" }}>
                Purchase Request: {modalData.pl_number || modalData.purchase_request_no}
              </div>
              <button style={styles.iconBtn} onClick={closeModal}>✕</button>
            </div>
            <div style={{ ...styles.modalBody, padding: "14px" }}>
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Product Type</label>
                  {!isEditMode && !isFieldVisible('product_type') ? (
                    <MaskedValue />
                  ) : (
                  <Select
                    styles={rsCompact}
                    options={productTypeOptions}
                    value={productTypeOptions.find(opt => opt.value === modalData.productType) || null}
                    isDisabled={!isEditMode || isFieldLocked('product_type')}
                    isClearable
                    onChange={(opt) => {
                      const newType = opt?.value || "";
                      // Changing Product Type invalidates whatever product/asset
                      // was already picked (they come from different tables), so
                      // clear the selected code + its dependent fields and start
                      // the category/sub-category filters over.
                      setModalData(prev => ({
                        ...prev,
                        productType: newType,
                        product_code: "",
                        productDescription: "",
                        description: "",
                        uom: "",
                        hsnCode: "",
                        productCategory: "",
                        subCategory: "",
                        product_class: "",
                        pirChars: [],
                        pirNumber: null
                      }));
                      setExtraProducts([]);
                      setModalFilterCategory(null);
                      setModalFilterSubCategory(null);
                      setPirChars([]);
                      setPirCharsPirId(null);
                    }}
                  />
                  )}
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-md-3">
                  <label style={styles.compactLabel}>Created By</label>
                  {!isFieldVisible('created_by') ? (
                    <MaskedValue />
                  ) : (
                  <input className="form-control" style={styles.compactInput} value={modalData.created_by || getCurrentUser()} disabled />
                  )}
                </div>
                <div className="col-md-3">
                  <label style={styles.compactLabel}>Created Date</label>
                  {!isFieldVisible('created_date') ? (
                    <MaskedValue />
                  ) : (
                  <input className="form-control" style={styles.compactInput} value={formatDateDisplay(modalData.created_at) || getTodayDateDisplay()} disabled />
                  )}
                </div>
                <div className="col-md-3">
                  <label style={styles.compactLabel}>Approved By</label>
                  {!isFieldVisible('approved_by') ? (
                    <MaskedValue />
                  ) : (
                  <input className="form-control" style={styles.compactInput} value={modalData.approved_by || modalData.approver_id || ""} disabled />
                  )}
                </div>
                <div className="col-md-3">
                  <label style={styles.compactLabel}>Approved Date</label>
                  {!isFieldVisible('approved_date') ? (
                    <MaskedValue />
                  ) : (
                  <input className="form-control" style={styles.compactInput} value={modalData.approval_status === "APPROVED" ? formatDateDisplay(modalData.action_date) : ""} disabled />
                  )}
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Purchase Organization</label>
                  {!isEditMode && !isFieldVisible('purchase_organization') ? (
                    <MaskedValue />
                  ) : (
                  <CreatableSelect styles={rsCompact} isClearable placeholder="Select or type Purchase Organization" isDisabled={!isEditMode || isFieldLocked('purchase_organization')} options={purchaseOrgOptions} value={modalData.purchase_organization ? { value: modalData.purchase_organization, label: modalData.purchase_organization } : null} onChange={(opt) => setModalData(prev => ({ ...prev, purchase_organization: opt?.value || "" }))} />
                  )}
                </div>
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Purchase Planner</label>
                  {!isEditMode && !isFieldVisible('purchase_planner') ? (
                    <MaskedValue />
                  ) : (
                  <CreatableSelect styles={rsCompact} isClearable placeholder="Select or type Purchase Planner" isDisabled={!isEditMode || isFieldLocked('purchase_planner')} options={purchasePlannerOptions} value={modalData.purchase_planner ? { value: modalData.purchase_planner, label: modalData.purchase_planner } : null} onChange={(opt) => setModalData(prev => ({ ...prev, purchase_planner: opt?.value || "" }))} />
                  )}
                </div>
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Location</label>
                  {!isEditMode && !isFieldVisible('location') ? (
                    <MaskedValue />
                  ) : (
                  <CreatableSelect styles={rsCompact} isClearable placeholder="Select or type Location" isDisabled={!isEditMode || isFieldLocked('location')} options={locationOptions} value={modalData.location ? { value: modalData.location, label: modalData.location } : null} onChange={(opt) => setModalData(prev => ({ ...prev, location: opt?.value || "" }))} />
                  )}
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Sales Order</label>
                  {/* Read-only rollup: for a planning-origin PR, backend resolves
                      every SO linked via planning_parent (child_number = this PR)
                      and joins them as "SO1, SO2". Manually created PRs (no
                      planning_elements origin) are always "N/A" — there's no
                      SO to roll up. Never editable here; it's derived, not entered. */}
                  <input
                    className="form-control"
                    style={styles.compactInput}
                    value={modalData.sales_order || "N/A"}
                    disabled
                  />
                </div>
              </div>
              <div style={{ ...styles.tabs, marginBottom: "10px" }}>
                {["product", "review", "docs", "change_log"]
                  // Hide "Review" / "Docs" / "Change Log" while a brand-new PR is
                  // still being created (no purchase_request_no / pl_number yet,
                  // i.e. not saved) — there's nothing to review, attach, or log yet.
                  // Additionally hide "Docs" specifically while a planning-origin
                  // PR is still an unfilled draft (see isUnsavedPlanningDraft) —
                  // it should only appear once the PR has actually been saved.
                  .filter(tab => tab === "product" || modalData.purchase_request_no || modalData.pl_number)
                  .filter(tab => tab !== "docs" || !isUnsavedPlanningDraft)
                  .map(tab => (
                    <button key={tab} className={`btn ${createActiveTab === tab ? "btn-secondary" : "btn-light"}`} style={styles.compactTabBtn} onClick={() => setCreateActiveTab(tab)}>
                      {tab === "product" ? "Product" : tab === "review" ? "Review" : tab === "docs" ? "Docs" : "Change Log"}
                    </button>
                  ))}
              </div>

              {createActiveTab === "product" && (
                <div>
                  {modalData.productType !== 'Asset' && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                      {[
                        { key: 'category', opts: modalCategoryOptions, label: 'Category' },
                        { key: 'subCategory', opts: modalSubCategoryOptions, label: 'Sub Category' },
                      ].map(({ key, opts, label }) => (
                        <div key={key} style={{ minWidth: 120, flex: '1 1 110px', maxWidth: 180, display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', letterSpacing: '0.02em', paddingLeft: 2 }}>
                            {label}
                          </span>
                          <Select
                            options={opts}
                            value={key === 'category' ? modalFilterCategory : modalFilterSubCategory}
                            isDisabled={key === 'subCategory' && !modalFilterCategory}
                            onChange={(opt) => {
                              if (key === 'category') { setModalFilterCategory(opt); setModalFilterSubCategory(null); }
                              else { setModalFilterSubCategory(opt); }
                            }}
                            isClearable
                            placeholder="Select..."
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              control: (base) => ({ ...base, minHeight: 30, fontSize: '12px' }),
                              valueContainer: (base) => ({ ...base, padding: '2px 6px', flexWrap: 'wrap' }),
                              input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '12px' }),
                              indicatorsContainer: (base) => ({ ...base, alignSelf: 'flex-start', paddingTop: 2 }),
                              dropdownIndicator: (base) => ({ ...base, padding: 2 }),
                              clearIndicator: (base) => ({ ...base, padding: 2 }),
                              placeholder: (base) => ({ ...base, fontSize: '12px', color: '#94a3b8', fontWeight: 400 }),
                              option: (base, state) => ({
                                ...base,
                                fontSize: '12px',
                                fontWeight: 400,
                                color: state.isSelected ? '#fff' : '#334155',
                                backgroundColor: state.isSelected ? '#475569' : state.isFocused ? '#f1f5f9' : '#fff',
                                padding: '6px 10px',
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                              menu: (base) => ({ ...base, fontSize: '12px' }),
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {(modalFilterCategory || modalFilterSubCategory) && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', marginTop: 6, paddingLeft: 2 }}>
                        {modalFilteredProductCount} product{modalFilteredProductCount === 1 ? '' : 's'} match
                      </div>
                    )}
                  </div>
                  )}
                  <div className="table-responsive" style={styles.tableContainer}>
                  {isCreateMode && (
                    <div className="d-flex justify-content-end mb-2">
                      <button
                        type="button"
                        className="btn btn-dark btn-sm"
                        disabled={!isEditMode}
                        onClick={() => {
                          setExtraProducts(prev => ([
                            ...prev,
                            {
                              _rowId: `row_${Date.now()}_${prev.length}`,
                              product_code: "", productDescription: "", uom: "",
                              hsnCode: "", special_remarks: "", requirement_qty: "", requirement_date: todayISO(),
                              productType: "", productCategory: "", subCategory: "", product_class: "",
                              pirNumber: null, pirChars: []
                            }
                          ]));
                        }}
                      >
                        + Add Product
                      </button>
                    </div>
                  )}
                  <table className="table" style={styles.erpTable}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.erpTh, width: 50 }}>Sl. No.</th>
                        <th style={styles.erpTh}>{modalData.productType === 'Asset' ? 'Asset Code' : 'Product Code'}</th>
                        <th style={styles.erpTh}>{modalData.productType === 'Asset' ? 'Asset Description' : 'Description'}</th>
                        <th style={styles.erpTh}>UOM</th>
                        <th style={styles.erpTh}>Qty</th>
                        <th style={styles.erpTh}>Requirement Date</th>
                        <th style={styles.erpTh}>HSN Code</th>
                        <th style={styles.erpTh}>Special Remarks</th>
                        {isCreateMode && <th style={{ ...styles.erpTh, width: 80 }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ ...styles.erpTd, textAlign: "center" }}>1</td>
                        <td style={styles.erpTd}>
                          <Select
                            styles={{
                              ...rsCompact,
                              container: (base) => ({ ...base, minWidth: "180px" }),
                              menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                            }}
                            menuPlacement="top"
                            menuPortalTarget={document.body}
                            options={modalTypeProductCodes.filter(p =>
                              (!modalFilterCategory || p.product?.productCategory === modalFilterCategory.value) &&
                              (!modalFilterSubCategory || p.product?.subCategory === modalFilterSubCategory.value)
                            )}
                            value={allCodeOptions.find(p => p.value === modalData.product_code) || null}
                            isDisabled={!isEditMode || isFieldLocked('product_code')}
                            onChange={async (selected) => {
                              const p = selected?.product || {};
                              setModalData(prev => ({
                                ...prev,
                                product_code: p.productCode || "",
                                productDescription: p.productDescription || "",
                                uom: p.uom || "",
                                hsnCode: p.hsnCode || "",
                                productType: p.productType || "",
                                productCategory: p.productCategory || "",
                                subCategory: p.subCategory || "",
                                product_class: p.productClass || ""
                              }));
                              if (selected?.value) {
                                try {
                                  // Characteristics come from the product's own Purchase Info Record
                                  const charsRes = await axios.get(`${PURCHASE_API}/purchase-request/pir-characteristics`, {
        headers: authHeaders(),
                                    params: { product_code: selected.value }
                                  });
                                  if (charsRes.data.success) {
                                    const chars = charsRes.data.characteristics || [];
                                    const pirNo = charsRes.data.pir_number || null;
                                    setPirChars(chars);
                                    setPirCharsPirId(pirNo);
                                    setModalData(prev => ({
                                      ...prev,
                                      pirChars: chars,
                                      pirNumber: pirNo
                                    }));
                                  }
                                } catch (e) { /* ignore */ }
                              } else {
                                setModalData(prev => ({ ...prev, pirChars: [], pirNumber: null }));
                                setPirChars([]);
                                setPirCharsPirId(null);
                              }
                            }}
                          />
                        </td>
                        <td style={styles.erpTd}>{!isFieldVisible('item_description') ? <MaskedValue /> : (modalData.productDescription || modalData.description || "—")}</td>
                        <td style={styles.erpTd}>{!isFieldVisible('uom') ? <MaskedValue /> : modalData.uom}</td>
                        <td style={styles.erpTd}>
                          <input type="number" step="0.001" min="0" className="form-control" style={{ fontSize: 13, height: 32, padding: "4px 8px" }} value={modalData.requirement_qty || ""} onChange={(e) => setModalData(prev => ({ ...prev, requirement_qty: e.target.value }))} disabled={!isEditMode || isFieldLocked('requirement_qty')} />
                        </td>
                        <td style={styles.erpTd}>
                          <DatePicker
                            selected={modalData.requirement_date ? new Date(formatDate(modalData.requirement_date)) : null}
                            onChange={(date) => setModalData(prev => ({ ...prev, requirement_date: formatDate(date) }))}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            style={{ fontSize: 13, height: 32, padding: "4px 8px" }}
                            disabled={!isEditMode || isFieldLocked('requirement_date')}
                          />
                        </td>
                        <td style={styles.erpTd}>{!isFieldVisible('hsn_code') ? <MaskedValue /> : modalData.hsnCode}</td>
                        <td style={styles.erpTd}>
                          <input
                            type="text"
                            className="form-control"
                            style={{ fontSize: 13, height: 32, padding: "4px 8px" }}
                            placeholder="Special Remarks"
                            value={modalData.special_remarks || ""}
                            onChange={(e) => setModalData(prev => ({ ...prev, special_remarks: e.target.value }))}
                            disabled={!isEditMode || isFieldLocked('special_remarks')}
                          />
                        </td>
                        {isCreateMode && (
                          <td style={{ ...styles.erpTd, textAlign: "center", whiteSpace: "nowrap" }}>
                            <button
                              type="button"
                              style={{ ...styles.iconBtn, color: "#16a34a", opacity: isEditMode ? 1 : 0.4 }}
                              disabled={!isEditMode}
                              title="Insert product below"
                              onClick={() => insertPRRowAfter(0)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            </button>
                            <button
                              type="button"
                              style={{ ...styles.iconBtn, color: "#dc2626", opacity: (isEditMode && extraProducts.length > 0) ? 1 : 0.4 }}
                              disabled={!isEditMode || extraProducts.length === 0}
                              title={extraProducts.length === 0 ? "At least one product line is required" : "Remove product"}
                              onClick={() => {
                                setExtraProducts(prev => {
                                  const [firstRow, ...rest] = prev;
                                  setModalData(prevModal => ({
                                    ...prevModal,
                                    product_code: firstRow.product_code,
                                    productDescription: firstRow.productDescription,
                                    uom: firstRow.uom,
                                    hsnCode: firstRow.hsnCode,
                                    special_remarks: firstRow.special_remarks,
                                    requirement_qty: firstRow.requirement_qty,
                                    requirement_date: firstRow.requirement_date,
                                    productType: firstRow.productType,
                                    productCategory: firstRow.productCategory,
                                    subCategory: firstRow.subCategory,
                                    product_class: firstRow.product_class,
                                    pirNumber: firstRow.pirNumber,
                                    pirChars: firstRow.pirChars
                                  }));
                                  return rest;
                                });
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                <path d="M10 11v6"></path>
                                <path d="M14 11v6"></path>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                              </svg>
                            </button>
                          </td>
                        )}
                      </tr>
                      {extraProducts.map((row, idx) => (
                        <tr key={row._rowId}>
                          <td style={{ ...styles.erpTd, textAlign: "center" }}>{idx + 2}</td>
                          <td style={styles.erpTd}>
                            <Select
                              styles={{
                                ...rsCompact,
                                container: (base) => ({ ...base, minWidth: "180px" }),
                                menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                              }}
                              menuPlacement="top"
                              menuPortalTarget={document.body}
                              options={modalTypeProductCodes.filter(p =>
                                (!modalFilterCategory || p.product?.productCategory === modalFilterCategory.value) &&
                                (!modalFilterSubCategory || p.product?.subCategory === modalFilterSubCategory.value)
                              )}
                              value={allCodeOptions.find(p => p.value === row.product_code) || null}
                              isDisabled={!isEditMode || isFieldLocked('product_code')}
                              onChange={async (selected) => {
                                const p = selected?.product || {};
                                setExtraProducts(prev => prev.map((r, i) => i !== idx ? r : {
                                  ...r,
                                  product_code: p.productCode || "",
                                  productDescription: p.productDescription || "",
                                  uom: p.uom || "",
                                  hsnCode: p.hsnCode || "",
                                  productType: p.productType || "",
                                  productCategory: p.productCategory || "",
                                  subCategory: p.subCategory || "",
                                  product_class: p.productClass || "",
                                  // reset until this row's own PIR lookup resolves below
                                  pirNumber: null, pirChars: []
                                }));

                                if (!selected?.value) return;

                                // Fetch THIS product's own characteristics,
                                // independent of row 0 / any other row.
                                try {
                                  const charsRes = await axios.get(`${PURCHASE_API}/purchase-request/pir-characteristics`, {
        headers: authHeaders(),
                                    params: { product_code: selected.value }
                                  });
                                  if (charsRes.data.success) {
                                    setExtraProducts(prev => prev.map((r, i) => i !== idx ? r : {
                                      ...r,
                                      pirChars: charsRes.data.characteristics || [],
                                      pirNumber: charsRes.data.pir_number || null
                                    }));
                                  }
                                } catch (e) { /* ignore */ }
                              }}
                            />
                          </td>
                          <td style={styles.erpTd}>{!isFieldVisible('item_description') ? <MaskedValue /> : (row.productDescription || "—")}</td>
                          <td style={styles.erpTd}>{!isFieldVisible('uom') ? <MaskedValue /> : row.uom}</td>
                          <td style={styles.erpTd}>
                            <input type="number" step="0.001" min="0" className="form-control" style={{ fontSize: 13, height: 32, padding: "4px 8px" }} value={row.requirement_qty || ""} onChange={(e) => { const v = e.target.value; setExtraProducts(prev => prev.map((r, i) => i !== idx ? r : { ...r, requirement_qty: v })); }} disabled={!isEditMode || isFieldLocked('requirement_qty')} />
                          </td>
                          <td style={styles.erpTd}>
                            <DatePicker
                              selected={row.requirement_date ? new Date(formatDate(row.requirement_date)) : null}
                              onChange={(date) => { const v = formatDate(date); setExtraProducts(prev => prev.map((r, i) => i !== idx ? r : { ...r, requirement_date: v })); }}
                              dateFormat="dd/MM/yyyy"
                              className="form-control"
                              style={{ fontSize: 13, height: 32, padding: "4px 8px" }}
                              disabled={!isEditMode || isFieldLocked('requirement_date')}
                            />
                          </td>
                          <td style={styles.erpTd}>{!isFieldVisible('hsn_code') ? <MaskedValue /> : row.hsnCode}</td>
                          <td style={styles.erpTd}>
                            <input
                              type="text"
                              className="form-control"
                              style={{ fontSize: 13, height: 32, padding: "4px 8px" }}
                              placeholder="Special Remarks"
                              value={row.special_remarks || ""}
                              onChange={(e) => { const v = e.target.value; setExtraProducts(prev => prev.map((r, i) => i !== idx ? r : { ...r, special_remarks: v })); }}
                              disabled={!isEditMode || isFieldLocked('special_remarks')}
                            />
                          </td>
                          {isCreateMode && (
                            <td style={{ ...styles.erpTd, textAlign: "center", whiteSpace: "nowrap" }}>
                              <button
                                type="button"
                                style={{ ...styles.iconBtn, color: "#16a34a", opacity: isEditMode ? 1 : 0.4 }}
                                disabled={!isEditMode}
                                title="Insert product below"
                                onClick={() => insertPRRowAfter(idx + 1)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                              </button>
                              <button
                                type="button"
                                style={{ ...styles.iconBtn, color: "#dc2626", opacity: isEditMode ? 1 : 0.4 }}
                                disabled={!isEditMode}
                                title="Remove product"
                                onClick={() => setExtraProducts(prev => prev.filter((_, i) => i !== idx))}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                  <path d="M10 11v6"></path>
                                  <path d="M14 11v6"></path>
                                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                                </svg>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {createActiveTab === "review" && (
                <div>
                  <div className="d-flex justify-content-end gap-2 mb-2">
                    {canApprove && (
                      <>
                        <button className="btn btn-dark btn-sm" onClick={handleApprove} disabled={reviewSelectedRows.size === 0 || isEditMode}>Approve</button>
                        <button className="btn btn-dark btn-sm" onClick={handleReject} disabled={reviewSelectedRows.size === 0 || isEditMode}>Reject</button>
                      </>
                    )}
                  </div>
                  <div className="table-responsive" style={styles.tableContainer}>
                    {(() => {
                      // Build the review row list once so the header "select all"
                      // and each row's checkbox agree on what's selectable —
                      // a line already Approved/Rejected can't be re-selected.
                      const reviewRows = [
                        { key: "row0", product_code: modalData.product_code, productDescription: modalData.productDescription || modalData.description || "", uom: modalData.uom, qty: modalData.requirement_qty || modalData.qty, requirement_date: modalData.requirement_date, hsnCode: modalData.hsnCode, approval_status: modalData.approval_status },
                        ...extraProducts.map(row => ({ key: row._rowId, product_code: row.product_code, productDescription: row.productDescription || "", uom: row.uom, qty: row.requirement_qty, requirement_date: row.requirement_date, hsnCode: row.hsnCode, approval_status: row.approval_status || "Pending For Approval" }))
                      ];
                      const selectableKeys = reviewRows.filter(r => r.approval_status === "Pending For Approval").map(r => r.key);
                      const allSelected = selectableKeys.length > 0 && selectableKeys.every(k => reviewSelectedRows.has(k));

                      const toggleRow = (key) => {
                        setReviewSelectedRows(prev => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key); else next.add(key);
                          return next;
                        });
                      };
                      const toggleAll = (checked) => {
                        setReviewSelectedRows(checked ? new Set(selectableKeys) : new Set());
                      };

                      return (
                        <table className="table" style={styles.erpTable}>
                          <thead>
                            <tr>
                              <th style={{ ...styles.erpTh, textAlign: "center" }}>
                                <input type="checkbox" checked={allSelected} onChange={e => toggleAll(e.target.checked)} disabled={selectableKeys.length === 0} />
                              </th>
                              <th style={styles.erpTh}>{modalData.productType === 'Asset' ? 'Asset Code' : 'Product Code'}</th>
                              <th style={styles.erpTh}>{modalData.productType === 'Asset' ? 'Asset Description' : 'Description'}</th>
                              <th style={styles.erpTh}>UOM</th>
                              <th style={styles.erpTh}>Qty</th>
                              <th style={styles.erpTh}>Requirement Date</th>
                              <th style={styles.erpTh}>HSN Code</th>
                              <th style={styles.erpTh}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reviewRows.map(row => (
                              <tr key={row.key}>
                                <td style={{ ...styles.erpTd, textAlign: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={reviewSelectedRows.has(row.key)}
                                    onChange={() => toggleRow(row.key)}
                                    disabled={row.approval_status !== "Pending For Approval"}
                                  />
                                </td>
                                <td style={styles.erpTd}>{!isFieldVisible('product_code') ? <MaskedValue /> : row.product_code}</td>
                                <td style={styles.erpTd}>{!isFieldVisible('item_description') ? <MaskedValue /> : row.productDescription}</td>
                                <td style={styles.erpTd}>{!isFieldVisible('uom') ? <MaskedValue /> : row.uom}</td>
                                <td style={styles.erpTd}>{!isFieldVisible('requirement_qty') ? <MaskedValue /> : row.qty}</td>
                                <td style={styles.erpTd}>{!isFieldVisible('requirement_date') ? <MaskedValue /> : formatDateDisplay(row.requirement_date)}</td>
                                <td style={styles.erpTd}>{!isFieldVisible('hsn_code') ? <MaskedValue /> : row.hsnCode}</td>
                                <td style={styles.erpTd}>{row.approval_status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* ================= DOCS TAB ================= */}
{createActiveTab === "docs" && (() => {
  // The PR form itself is auto-generated (same pattern as Goods Receipt's
  // Docs tab) — only offer it once the PR actually has a number, i.e. it's
  // been saved/submitted at least once. A brand-new, unsaved PR has nothing
  // to show/print yet.
  const currentPrNo = modalData?.purchase_request_no || modalData?.pl_number || "";
  const prPdfFilename = currentPrNo ? `${currentPrNo}_PurchaseRequisition.pdf` : "";
  const prViewUrl = currentPrNo ? `/cable/procurement/purchase-request/view/${currentPrNo}` : "";

  return (
  <div style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>

    {/* Auto-generated PR form — View / Download / Print, same as Goods Receipt */}
    {currentPrNo ? (
      <div style={{ marginBottom: 16 }}>
        <div style={{ border: "1px solid #dee2e6", borderRadius: "6px", padding: "16px", background: "#fff" }}>
          <div style={{ marginBottom: "12px" }}>
            <span style={{ fontSize: "18px", marginRight: "8px" }}>📄</span>
            <span style={{ fontSize: "14px", fontWeight: 500, color: "#0f172a" }}>
              {prPdfFilename}
            </span>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button className="btn btn-dark btn-sm" onClick={() => setShowPRDocInModal(true)}>
              View
            </button>
            <button
              className="btn btn-dark btn-sm"
              onClick={() => window.open(`${prViewUrl}?action=download`, "_blank")}
            >
              Download
            </button>
            <button
              className="btn btn-outline-dark btn-sm"
              onClick={() => window.open(`${prViewUrl}?action=print`, "_blank")}
            >
              Print
            </button>
          </div>
        </div>

        {showPRDocInModal && (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowPRDocInModal(false); }}
          >
            <div style={{ width: "90%", height: "90%", background: "#fff", borderRadius: "8px", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #e2e8f0" }}>
                <span style={{ fontWeight: 600, fontSize: "14px" }}>{prPdfFilename}</span>
                <button onClick={() => setShowPRDocInModal(false)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>
                  ✕
                </button>
              </div>
              <iframe
                src={prViewUrl}
                title="Purchase Requisition"
                width="100%"
                style={{ flex: 1, border: "none" }}
              />
            </div>
          </div>
        )}
      </div>
    ) : (
      <div style={{ marginBottom: 16 }}>
        <div style={{ border: "1px dashed #cbd5e1", borderRadius: "6px", padding: "16px", textAlign: "center", color: "#64748b" }}>
          Save the PR to get a PR number before the form can be viewed, downloaded, or printed.
        </div>
      </div>
    )}

    {/* Upload drop zone — same look as Business Partner / Sales Order */}
    <div style={{ marginBottom: 16 }}>
      <label
        htmlFor="prDocFileInput"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", border: "2px dashed #cbd5e1", borderRadius: 8,
          padding: "18px 12px", cursor: "pointer",
          background: "#f8fafc"
        }}
        onDragOver={(e) => e.preventDefault()}
      >
        <span style={{ fontSize: 22 }}>📁</span>
        <span style={{ fontSize: 13, color: "#475569", fontWeight: 500, marginTop: 4 }}>
          {docsUploading ? "Uploading…" : "Click to attach documents"}
        </span>
        <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
          PDF, DOCX, XLSX (max {maxPrDocSizeKb} KB per file)
        </span>
        <input
          id="prDocFileInput"
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleDocUpload}
        />
      </label>
    </div>

    {/* Uploaded files list — pulled from the server (Google Drive-backed) */}
    {uploadedDocs.length === 0 ? (
      <div style={{ textAlign: "center", padding: "28px 0", color: "#94a3b8", fontSize: 13 }}>
        No documents attached yet.
      </div>
    ) : (
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            <th style={styles.erpTh}>#</th>
            <th style={styles.erpTh}>File Name</th>
            <th style={styles.erpTh}>Size</th>
            <th style={styles.erpTh}>Uploaded At</th>
            <th style={styles.erpTh}></th>
          </tr>
        </thead>
        <tbody>
          {uploadedDocs.map((doc, idx) => (
            <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
              <td style={styles.erpTd}>{idx + 1}</td>
              <td style={styles.erpTd}>
                <span style={{ color: "#1e293b", fontWeight: 500 }}>{doc.originalname || doc.filename}</span>
              </td>
              <td style={styles.erpTd}>
                {doc.size
                  ? doc.size > 1024 * 1024
                    ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB`
                    : `${(doc.size / 1024).toFixed(1)} KB`
                  : '—'}
              </td>
              <td style={styles.erpTd}>{doc.uploadedAt ? formatChangeLogDateTime(doc.uploadedAt) : '—'}</td>
              <td style={styles.erpTd}>
                <button
                  type="button"
                  onClick={() => handleDocDownload(doc)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    color: '#2563eb', fontWeight: 600, fontSize: 12,
                    textDecoration: 'none', padding: '3px 10px',
                    border: '1px solid #bfdbfe', borderRadius: 5,
                    background: '#eff6ff', cursor: 'pointer'
                  }}
                >
                  ⬇ Download
                </button>
              </td>
            </tr>
          ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
})()}

              {/* ================= CHANGE LOG TAB ================= */}
              {createActiveTab === "change_log" && (
                <div style={styles.tableContainer}>
                  <table className="table" style={styles.erpTable}>
                    <thead>
                      <tr>
                        <th style={styles.erpTh}>Date & Time</th>
                        <th style={styles.erpTh}>User</th>
                        <th style={styles.erpTh}>Action</th>
                        <th style={styles.erpTh}>Field</th>
                        <th style={styles.erpTh}>Old Value</th>
                        <th style={styles.erpTh}>New Value</th>
                        <th style={styles.erpTh}>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changeLogs.length > 0 ? (
                        changeLogs.map((log, idx) => {
                          const dateVal = log.created_at || log.updated_at;
                          return (
                            <tr key={idx}>
                              <td style={styles.erpTd}>
                                {formatChangeLogDateTime(dateVal)}
                              </td>
                              <td style={styles.erpTd}>{log.created_by || log.updated_by || "System"}</td>
                              <td style={styles.erpTd}>{log.action_type || "Update"}</td>
                              <td style={styles.erpTd}>
                                {log.field_name
                                  ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                  : '-'}
                              </td>
                              <td style={{ ...styles.erpTd, maxWidth: "150px" }}>{log.old_value || "-"}</td>
                              <td style={{ ...styles.erpTd, maxWidth: "150px" }}>{log.new_value || "-"}</td>
                              <td style={{ ...styles.erpTd, maxWidth: "200px", wordWrap: "break-word" }}>{log.comment || "-"}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" style={{ ...styles.erpTd, textAlign: "center", padding: 20 }}>
                            No changes recorded
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ ...styles.modalFooter, padding: "10px 14px", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
              <div className="d-flex gap-2">
                {modalData.pl_number ? (
                  <>
                    {modalData.approval_status === "CREATED" && (
                      <button className="btn btn-dark btn-sm" onClick={handleSubmitPR}>Save</button>
                    )}
                    {modalData.approval_status !== "CREATED" && !isEditMode && canEdit && <button className="btn btn-dark btn-sm" onClick={() => setIsEditMode(true)}>Edit</button>}
                    {modalData.approval_status !== "CREATED" && isEditMode && (
                      <>
                        <button className="btn btn-dark btn-sm" onClick={handleSaveAndResubmit}>Save</button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {modalData.approval_status === "CREATED" && <button className="btn btn-dark btn-sm" onClick={handleSubmitPR}>Submit</button>}
                    {!isEditMode ? (
                      <button className="btn btn-dark btn-sm" onClick={() => setIsEditMode(true)}>Edit</button>
                    ) : (
                      <button className="btn btn-dark btn-sm" onClick={async () => { const ok = await handleSave(); if (ok) setIsEditMode(false); }}>Save</button>
                    )}
                  </>
                )}
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Comment Modal ── */}
      {commentModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: "500px" }}>
            <div style={styles.modalHeader}>
              <strong>{actionType === "APPROVED" ? "Approved" : actionType === "REJECTED" ? "Reject" : actionType}</strong>
              <button style={styles.iconBtn} onClick={() => setCommentModal(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <label style={styles.compactLabel}>Comment / Remarks{" "}{actionType === "REJECTED" ? <span style={{ color: "red" }}>*</span> : <span style={{ color: "#888", fontWeight: "normal" }}>(optional)</span>}</label>
              <textarea className="form-control" rows={4} value={comment} onChange={e => setComment(e.target.value)} placeholder="Enter your comment here" />
            </div>
            <div style={styles.modalFooter}>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setCommentModal(false)}>Cancel</button>
              <button className="btn btn-dark btn-sm" onClick={handleReviewSubmit}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Supplier Conflict Dialog ── shown when selected PRs have different suppliers */}
      {showSupplierDialog && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.conversionModal, maxWidth: 680 }}>
            <div style={styles.conversionHeader}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>Multiple Suppliers Found</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                The selected Purchase Requests have different suppliers. Choose how to proceed.
              </div>
            </div>
            <div style={styles.conversionBody}>
              <table style={{ ...styles.erpTable, marginBottom: 4 }}>
                <thead>
                  <tr>
                    <th style={styles.erpTh}>PR No</th>
                    <th style={styles.erpTh}>Product Code</th>
                    <th style={styles.erpTh}>Product Description</th>
                    <th style={styles.erpTh}>Supplier</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierDialogItems.map((item, idx) => (
                    <tr key={idx}>
                      <td style={styles.erpTd}>{item.purchase_request_no}</td>
                      <td style={styles.erpTd}>{item.product_code}</td>
                      <td style={styles.erpTd}>{item.product_description}</td>
                      <td style={styles.erpTd}>{item.vendorName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px dashed #e5e7eb" }}>
                <label style={styles.compactLabel}>To convert everything as ONE PO, pick a single supplier:</label>
                <Select
                  options={supplierDialogOptions.map(o => ({
                    value: o.vendorCode || o.vendorName,
                    label: o.vendorName,
                    raw: o
                  }))}
                  value={selectedForcedSupplier
                    ? { value: selectedForcedSupplier.vendorCode || selectedForcedSupplier.vendorName, label: selectedForcedSupplier.vendorName }
                    : null}
                  onChange={(opt) => setSelectedForcedSupplier(opt ? opt.raw : null)}
                  placeholder="Select supplier..."
                  isClearable
                  menuPlacement="bottom"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    ...rsCompact,
                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                  }}
                />
              </div>
            </div>
            <div style={{ ...styles.modalFooter, justifyContent: "space-between" }}>
              <button className="btn btn-outline-secondary btn-sm" onClick={closeSupplierDialog}>Cancel</button>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-dark btn-sm" onClick={handleContinueDifferentSuppliers}>
                  Continue with Different Suppliers
                </button>
                <button className="btn btn-dark btn-sm" disabled={!selectedForcedSupplier} onClick={handleConvertAsOnePO}>
                  Convert as One PO
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConversionModal && conversionResult && (
        <div style={styles.modalBackdrop}>
          <div style={styles.conversionModal}>
            <div style={styles.conversionHeader}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>Conversion Result</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                {conversionResult.data.length} of {conversionResult.total ?? conversionResult.data.length} converted successfully
              </div>
            </div>
            <div style={styles.conversionBody}>
              {conversionResult.data.length === 0 ? (
                <div style={{ fontSize: 13, color: "#64748b" }}>{conversionResult.message}</div>
              ) : (
                conversionResult.data.map((row, idx) => (
                  <div key={idx} style={styles.conversionRow}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span>
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>{row.prNo}</span>
                      <span style={{ color: "#64748b" }}>→</span>
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>{row.poId}</span>
                    </div>
                    <div style={{ color: "#16a34a", fontSize: 12, marginLeft: 22 }}>
                      Purchase order created successfully
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={styles.modalFooter}>
              <button className="btn btn-dark" style={{ padding: "6px 20px" }} onClick={closeConversionModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </Layout>
  );
}

const styles = {
  compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: "#334155" },
  compactInput: { fontSize: "14px", height: "32px", padding: "4px 8px" },
  compactTabBtn: { fontSize: "14px", padding: "4px 10px" },
  pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  pageTitle: { fontSize: 26, fontWeight: 700, color: '#1e293b', marginBottom: 2 },
  pageSubtitle: { fontSize: 13, color: '#64748b' },
  filterCard: { background: '#ffffff', borderRadius: 8, padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 14 },
  filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 2, color: '#334155', display: 'block' },
  primaryBtn: { paddingLeft: 14, paddingRight: 14 },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px', overflowY: 'auto' },
  conversionModal: { background: '#fff', borderRadius: '10px', width: '90%', maxWidth: '480px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' },
  conversionHeader: { padding: '18px 22px 14px', borderBottom: '1px solid #eef0f2' },
  conversionBody: { padding: '16px 22px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 },
  conversionRow: { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13, padding: '6px 0', borderBottom: '1px dashed #f1f5f9' },
  modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  iconBtn: { background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer' },
  modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  tabs: { display: 'flex', gap: 8, margin: '14px 0' },
  tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
  erpTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
  erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
  prLink: { fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", color: "#2563eb", textDecoration: "underline", cursor: "pointer", background: "none", border: "none", padding: 0 },
};

export default PurchaseRequisition;